// /pipeline — two-column source ↔ target visualization.
//
// Layout:
//   left column = source cards
//   right column = target cards
//   SVG layer underneath draws straight bezier lines for each existing
//   source↔target connection.
//
// Interaction:
//   - Click "View" on a card → modal with adapter type, connection, schema (+ sample for sources)
//   - Click a connection line → side panel with the field mappings + processing rules
//   - Drag from a source card's right edge to a target card's left edge → opens the field mapper

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isPortal } from "../utils/portal";
import { createPageUrl } from "@/utils";
import { useAuth } from "../contexts/AuthContext";
import { useProject } from "../contexts/ProjectContext";
import PipelineCanvas from "../components/pipeline/PipelineCanvas";
import ConfigAppModal from "../components/pipeline/ConfigAppModal";
import GuidedChooser, { startGuided } from "../components/guided/GuidedChooser";
import {
  getPipelineOverview,
  getSourceSchema,
  getTargetSchema,
  createPipelineEdge,
  deletePipelineEdge,
  listProcessingRules,
  runPipeline,
  listPipelineJobs,
  getPipelineJob,
  readRecords,
  listStrategyKinds,
  deleteStrategy,
  createStrategy,
  updateStrategy,
  updateNode,
  listSources,
  placeSource,
  getNodeCatalog,
  createSourceNode,
  createTargetNode,
  deleteNode,
  getUploadSignedUrl,
  stripUtf8Bom,
  listNodes,
  listWorkflows,
  createWorkflow,
  renameWorkflow,
  deleteWorkflow,
  getStorageConfig,
  updateStorageConfig,
  listStorageMappings,
} from "../services/pipeline";
import { DataElementView, SourceFieldMapping } from "../entities/all";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useConfirm } from "@/components/ui/confirm-dialog";
import {
  Database, FileText, Cloud, Server, HardDrive, Settings2, Boxes,
  Plus, RefreshCw, Eye, X, ChevronRight, Trash2, Loader2,
  CheckCircle2, Play, Clock, AlertCircle,
  ArrowUp, ArrowDown, ArrowUpDown, ArrowRight, ChevronLeft, ExternalLink,
  ChevronDown, ChevronUp, Sparkles, Wrench, PanelLeftClose, PanelLeftOpen, GitBranch,
} from "lucide-react";
import AppRequestDialog from "../components/AppRequestDialog";

// ── request kinds (the "Build with an agent" pattern, same as Apps/Agents/
// Workflows) — an agent builds/changes a GOVERNED pipeline in this project ──
const PIPELINE_REQUEST_KINDS = [
  {
    kind: "new_pipeline", label: "New pipeline", icon: Sparkles, suggestFeatures: true,
    hint: "Describe the data and where it should land — an agent builds the governed pipeline: sources, data elements, mappings, processing rules, target, and a verified run.",
    titlePlaceholder: "What should the pipeline be called / do?",
    detailsPlaceholder: "Describe the data in plain language — what it looks like, where it comes from, where it should land, any rules…",
    allowFile: true,
    fileHint: "Attach a data description, sample, or archive (.docx, .txt, .md, .csv, .xlsx, .zip, images)",
  },
  {
    kind: "change_pipeline", label: "Change a pipeline", icon: Wrench,
    hint: "Describe the change to an existing pipeline (name it, or use the ✨ action on a pipeline row to prefill).",
    titlePlaceholder: "Short summary",
    detailsPlaceholder: "Which pipeline, and what should change — new sources, mappings, rules, targets…",
  },
];

// Map adapter kind → icon + human label
const ADAPTER_INFO = {
  postgres:   { icon: Database, label: "Postgres",  color: "#377AD6" },
  mysql:      { icon: Database, label: "MySQL",     color: "#00758F" },
  csv_upload: { icon: FileText, label: "CSV upload", color: "#85D266" },
  xlsx:       { icon: FileText, label: "Excel (S3)", color: "#21A366" },
  s3:         { icon: Cloud,    label: "S3",        color: "#FF9900" },
  legacy:     { icon: HardDrive,label: "Legacy",    color: "#94a3b8" },
  default:    { icon: Server,   label: "Unknown",   color: "#94a3b8" },
};
const adapterInfo = (kind) => ADAPTER_INFO[kind] || ADAPTER_INFO.default;

// Frontend-only synthesizer for the ?mock=N stress test. Generates a
// recipe shaped like getPipelineOverview()'s response so the canvas
// renders without any backend involvement.
function buildMockPipeline(n) {
  const kinds = ['postgres', 'mysql', 'csv_upload', 'xlsx', 's3'];
  const sources = Array.from({ length: n }, (_, i) => ({
    id: i + 1,
    source_name: `src_${String(i).padStart(3, '0')}`,
    kind: kinds[i % kinds.length],
    description: `Synthetic source #${i}`,
    status: 'Active',
  }));
  const targetCount = Math.max(3, Math.min(10, Math.round(n / 20)));
  const targets = Array.from({ length: targetCount }, (_, i) => ({
    id: i + 1,
    name: `tgt_${i + 1}`,
    kind: 'postgres',
    description: `Synthetic target #${i + 1}`,
    status: 'Active',
  }));
  // Each source fans to 1-3 random targets; each connection carries
  // 1-2 (source_field, target_field) mapping rows so edge bundling
  // gets exercised too.
  const connections = [];
  // Deterministic pseudo-random so the layout is stable across reloads.
  let seed = 0xC0FFEE;
  const rand = () => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 0xFFFFFFFF; };
  for (const s of sources) {
    const fanOut = 1 + Math.floor(rand() * 3);
    const picked = new Set();
    for (let i = 0; i < fanOut; i++) {
      const t = targets[Math.floor(rand() * targets.length)];
      const key = `${s.id}:${t.id}`;
      if (picked.has(key)) continue;
      picked.add(key);
      const mappingCount = 1 + Math.floor(rand() * 2);
      const mappings = Array.from({ length: mappingCount }, (_, m) => ({
        edge_id: `mock:${s.id}:${t.id}:${m}`,
        synthetic: false,
        source_field: `col_${m + 1}`,
        target_field: `tbl_a.col_${m + 1}`,
        rule: null,
        element: null,
      }));
      connections.push({
        source_id: s.id,
        target_id: t.id,
        target_table: 'tbl_a',
        mappings,
      });
    }
  }
  // Also synthesize the storages-strategies DAG so ?mock= exercises the
  // PipelineCanvas path (file → parquet_raw → parquet_processed → postgres
  // per source, converging on one postgres target).
  const storages = [];
  const strategies = [];
  let sid = 0;
  const pgStorageId = 'postgres:1';
  for (const s of sources) {
    const fileId = `file:${++sid}`;
    const rawId = `parquet_raw:${++sid}`;
    const procId = `parquet_processed:${++sid}`;
    storages.push({ storage_id: fileId, storage_type: 'file', name: s.source_name, entity_id: s.id });
    // Parquet storages are pipeline artifacts of the owning data source — carry
    // its id so the inspector's Records tab can read the parquet for that source.
    storages.push({ storage_id: rawId, storage_type: 'parquet_raw', name: `${s.source_name} (raw parquet)`, entity_id: s.id });
    storages.push({ storage_id: procId, storage_type: 'parquet_processed', name: `${s.source_name} (processed)`, entity_id: s.id });
    strategies.push({ id: `m-f2p-${s.id}`, kind: 'file_to_parquet', domain_storage_id: fileId, codomain_storage_id: rawId });
    strategies.push({ id: `m-rules-${s.id}`, kind: 'processing_rules', domain_storage_id: rawId, codomain_storage_id: procId });
    strategies.push({ id: `m-load-${s.id}`, kind: 'relational_load', domain_storage_id: procId, codomain_storage_id: pgStorageId });
  }
  storages.push({ storage_id: pgStorageId, storage_type: 'postgres', name: targets[0]?.name || 'warehouse' });
  return { sources, targets, connections, storages, strategies };
}

export default function Pipeline() {
  const { user } = useAuth();
  const { projectSlug } = useProject();
  const navigate = useNavigate();
  const { confirm, prompt } = useConfirm();
  const [data, setData] = useState({ sources: [], targets: [], connections: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [viewEntity, setViewEntity] = useState(null); // {kind:'source'|'target', row}
  const [isTargetChooserOpen, setIsTargetChooserOpen] = useState(false);
  const [activeConnection, setActiveConnection] = useState(null); // {source_id, target_id}
  const [mapperState, setMapperState] = useState(null); // {source, target} when creating
  const [activeStorage, setActiveStorage] = useState(null); // storage node clicked in the DAG
  const [activeStrategy, setActiveStrategy] = useState(null); // strategy edge clicked in the DAG
  const [strategyKinds, setStrategyKinds] = useState([]);     // registry (impl_endpoint per kind)
  const [catalog, setCatalog] = useState({ connectors: [], transforms: [], strategies: [] }); // unified node catalog
  const [connectorForm, setConnectorForm] = useState(null);   // {connector} when configuring a dropped connector
  const [workflows, setWorkflows] = useState([]);             // [{id,name,position}]
  // Active workflow is reflected in the URL (?workflow=<id>) so a pipeline tab is
  // deep-linkable and survives reload. Seed from the URL on mount.
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeWf, setActiveWf] = useState(() => {
    const v = Number(searchParams.get('workflow'));
    return Number.isFinite(v) && v > 0 ? v : null;
  });
  const [flowProjectId, setFlowProjectId] = useState(null);   // the FORK's flow project — its endpoint view shows inherited kinds too; the prototype is org-locked
  const [forkProjectId, setForkProjectId] = useState(null);   // THIS project's own fork (where config apps live)
  const [configApp, setConfigApp] = useState(null);           // { node, configView } — config-app modal

  const [railOpen, setRailOpen] = useState(() => localStorage.getItem("pipeline_rail_open") !== "0");
  const [buildDialog, setBuildDialog] = useState(null); // { preset? }
  const [buildFiled, setBuildFiled] = useState(false);
  const toggleRail = () => setRailOpen((v) => { localStorage.setItem("pipeline_rail_open", v ? "0" : "1"); return !v; });

  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState(null);
  const [jobs, setJobs] = useState({ total: 0, rows: [] });
  const [jobsPage, setJobsPage] = useState(0);
  const [jobsOpen, setJobsOpen] = useState(false); // runs drawer collapsed by default
  const JOBS_PER_PAGE = 10;
  const [openJobId, setOpenJobId] = useState(null);

  // ?mock=N — synthesize N synthetic sources, ~5 targets, fan-out edges
  // for stress-testing the canvas. Bypasses the backend so the new view
  // can be verified at hundreds of nodes without depending on real recipe
  // data. Default (no param) loads the real pipeline overview unchanged.
  const mockN = (() => {
    const v = Number(new URLSearchParams(window.location.search).get('mock'));
    return Number.isFinite(v) && v > 0 ? Math.min(v, 1000) : 0;
  })();

  // `quiet` = background live-refresh: refetch + swap the DAG in without flipping
  // the full-page loading state, so the graph doesn't blink every few seconds while
  // a guided run wires it. Only the initial / manual load shows the loading state.
  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) { setLoading(true); setError(null); }
    try {
      if (mockN > 0) {
        setData(buildMockPipeline(mockN));
      } else {
        // The pipeline is the NODE/LINK graph of the ACTIVE WORKFLOW. Load the
        // workflow list, pick/keep an active one, and load that workflow's nodes.
        const wfRes = await listWorkflows().catch(() => ({ workflows: [] }));
        const wfs = wfRes.workflows || [];
        setWorkflows(wfs);
        let wfId = activeWf;
        if (wfId == null || !wfs.some(w => w.id === wfId)) wfId = wfs[0]?.id ?? null;
        if (wfId !== activeWf) setActiveWf(wfId);
        const [overview, ng, srcRes] = await Promise.all([
          getPipelineOverview(),
          listNodes(wfId).catch(() => ({ nodes: [], links: [] })),
          listSources().catch(() => ({ sources: [] })),
        ]);
        setData({ ...overview, nodeGraph: ng, existingSources: srcRes?.sources || [] });
      }
    }
    catch (e) { if (!quiet) setError(e?.message || "Failed to load pipeline"); }
    finally { if (!quiet) setLoading(false); }
  }, [mockN, activeWf]);
  useEffect(() => { load(); }, [load]);

  // Reflect the active workflow in the URL (?workflow=<id>), replace (no history
  // spam). Only writes when the value actually differs, so it won't loop with the
  // mount-time seed. Preserves any other query params (e.g. ?mock=).
  useEffect(() => {
    if (mockN > 0) return; // mock mode doesn't use real workflows
    const cur = searchParams.get('workflow');
    const want = activeWf != null ? String(activeWf) : null;
    if (cur === want) return;
    const next = new URLSearchParams(searchParams);
    if (want) next.set('workflow', want); else next.delete('workflow');
    setSearchParams(next, { replace: true });
  }, [activeWf, mockN, searchParams, setSearchParams]);

  // Strategy kinds give us each kind's impl_endpoint + the prototype flow
  // project id, so the strategy inspector can embed/open its Flow implementation.
  useEffect(() => {
    let cancelled = false;
    listStrategyKinds().then(r => {
      if (cancelled) return;
      setStrategyKinds(r?.strategy_kinds || []);
      setFlowProjectId(r?.flow_project_id || null);
      setForkProjectId(r?.fork_project_id || null);
    }).catch(() => {});
    getNodeCatalog().then(r => { if (!cancelled) setCatalog({ connectors: r?.connectors || [], transforms: r?.transforms || [], strategies: r?.strategies || r?.transforms || [] }); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const loadJobs = useCallback(async () => {
    try {
      const r = await listPipelineJobs({ limit: JOBS_PER_PAGE, offset: jobsPage * JOBS_PER_PAGE });
      setJobs(r);
    } catch { /* swallow — jobs panel is best-effort */ }
  }, [jobsPage]);
  useEffect(() => { loadJobs(); }, [loadJobs]);

  // Delete a strategy node (unwire one transformation) from the canvas, then
  // refresh the DAG. Confirm first — it changes the recipe.
  const handleDeleteStrategy = useCallback(async (m) => {
    if (m?.id == null) return;
    const ok = await confirm({
      title: `Delete transformation "${m.kind}"?`,
      description: `${m.domain_storage_id} → ${m.codomain_storage_id}. This removes it from the pipeline.`,
      confirmText: "Delete",
    });
    if (!ok) return;
    try {
      await deleteStrategy(m.id);
      await load();
    } catch (e) {
      setError(`Failed to delete transformation: ${e?.message || e}`);
    }
  }, [load, confirm]);

  // Submit a connector config form → create the source/target node, then reload.
  // For Load File: sign an upload URL, PUT the file to S3, then create the source
  // from the resulting s3_uri (server copies + analyzes). For db connectors: post
  // the connection fields straight through.
  // ── Workflow tab actions ──────────────────────────────────────────────────
  // Add a workflow with a default name + open it; the tab/panel lets the user
  // rename inline (IDE-style) — no prompt.
  const handleAddWorkflow = useCallback(async () => {
    try { const r = await createWorkflow(`Workflow ${workflows.length + 1}`); setActiveWf(r.workflow_id); /* load() fires on activeWf change */ }
    catch (e) { setError(`Failed to add workflow: ${e?.message || e}`); }
  }, [workflows.length]);
  // Inline rename: the tab strip / side panel commit the new name → persist + reload.
  const handleRenameWorkflow = useCallback(async (id, name) => {
    if (!name) return;
    try { await renameWorkflow(id, name); await load(); }
    catch (e) { setError(`Failed to rename workflow: ${e?.message || e}`); }
  }, [load]);
  const handleDeleteWorkflow = useCallback(async (wf) => {
    if (workflows.length <= 1) { setError("Can't delete the only workflow."); return; }
    if (!(await confirm({ title: `Delete workflow "${wf.name}"?`, description: "This permanently removes the workflow and all its nodes.", confirmText: "Delete" }))) return;
    try { await deleteWorkflow(wf.id); if (activeWf === wf.id) setActiveWf(null); await load(); }
    catch (e) { setError(`Failed to delete workflow: ${e?.message || e}`); }
  }, [workflows.length, activeWf, load, confirm]);

  const handleConnectorSubmit = useCallback(async (connector, values) => {
    try {
      if (connector.role === 'load' && connector.node_type === 'load_file') {
        const rawFile = values.__file;
        if (!rawFile) { setError('Choose a file to upload.'); return; }
        const file = await stripUtf8Bom(rawFile);
        const sign = await getUploadSignedUrl(file.name, file.type || undefined);
        const putUrl = sign.signed_put_url || sign.url;
        if (!putUrl) { setError('Upload URL not returned by server.'); return; }
        const put = await fetch(putUrl, { method: 'PUT', headers: sign.content_type ? { 'Content-Type': sign.content_type } : undefined, body: file });
        if (!put.ok) { setError(`Upload failed (HTTP ${put.status}).`); return; }
        await createSourceNode({ kind: 'file', s3_uri: sign.s3_uri, source_name: values.source_name || file.name, workflow_id: activeWf, position: connectorForm?.position });
      } else if (connector.role === 'load') {
        await createSourceNode({ kind: connector.node_type.replace(/^source_/, ''), ...values, workflow_id: activeWf, position: connectorForm?.position });
      } else if (connector.role === 'save') {
        await createTargetNode({ kind: connector.node_type.replace(/^save_/, ''), ...values, workflow_id: activeWf, position: connectorForm?.position });
      }
      setConnectorForm(null);
      await load();
    } catch (e) {
      setError(`Failed to add ${connector.label}: ${e?.message || e}`);
    }
  }, [load, activeWf, connectorForm]);

  // Delete a node (load/save/transform) from the node-native canvas.
  const handleDeleteNode = useCallback(async (n) => {
    if (n?.id == null) return;
    const label = n.label || n.node_type || `node #${n.id}`;
    if (!(await confirm({ title: `Delete node "${label}"?`, description: "This removes it (and its links) from the pipeline.", confirmText: "Delete" }))) return;
    try { await deleteNode(n.id); await load(); }
    catch (e) { setError(`Failed to delete node: ${e?.message || e}`); }
  }, [load, confirm]);

  // Create a strategy from a completed canvas connection. When the kind is
  // ambiguous (>1 kind matches the typed endpoints), prompt the user to choose.
  const handleCreateStrategy = useCallback(async (spec) => {
    if (!spec) return;
    let kind = spec.kind;
    if (spec.ambiguous) {
      const choices = spec.candidates || [];
      if (!choices.length) {
        setError(`No transformation kind matches ${spec.domainType} → ${spec.codomainType}.`);
        return;
      }
      const pick = await prompt({
        title: "Choose a transformation",
        description: `Multiple transformation kinds match ${spec.domainType} → ${spec.codomainType}. Pick one to apply.`,
        label: "Transformation kind",
        options: choices,
        defaultValue: choices[0],
        confirmText: "Wire it",
      });
      if (!pick) return;
      if (!choices.includes(pick)) { setError(`"${pick}" is not one of: ${choices.join(", ")}`); return; }
      kind = pick;
    }
    try {
      await createStrategy({ kind, domain_storage_id: spec.domain_storage_id, codomain_storage_id: spec.codomain_storage_id, workflow_id: activeWf, position: spec.position });
      await load();
    } catch (e) {
      setError(`Failed to wire transformation: ${e?.message || e}`);
    }
  }, [load, activeWf, prompt]);

  // Place an existing source (from the library) as a node in the active workflow.
  const handlePlaceSource = useCallback(async (spec) => {
    if (!spec?.origin || spec.entity_id == null) return;
    try {
      await placeSource({ origin: spec.origin, entity_id: spec.entity_id, workflow_id: activeWf, position: spec.position });
      await load();
    } catch (e) {
      setError(`Failed to place source: ${e?.message || e}`);
    }
  }, [load, activeWf]);

  // Live refresh: re-fetch the DAG + jobs on cortex:data-changed (broadcast by the
  // Guided demo) so the pipeline graph fills in as storages/strategies are wired.
  useEffect(() => {
    const onChanged = () => { load({ quiet: true }); loadJobs(); };
    window.addEventListener("cortex:data-changed", onChanged);
    return () => window.removeEventListener("cortex:data-changed", onChanged);
  }, [load, loadJobs]);

  // Always poll the jobs list. Faster cadence (1.5s) while any row is
  // in_progress so the UI feels live during a run; slower (5s) otherwise
  // so the table still picks up runs started by other sessions or by
  // automated triggers (cron, MCP, etc.) without manual refresh.
  // The DAG itself is polled too (quietly — data swaps in with no loading
  // flip), at the slow cadence: a pipeline wired by the assistant, another
  // session, or a guided run whose overlay has closed would otherwise leave
  // this page showing a stale empty graph while the runs table stays fresh.
  useEffect(() => {
    const hasInProgress = jobs.rows.some(j => j.status === 'in_progress');
    const interval = hasInProgress ? 1500 : 5000;
    let tick = 0;
    const t = setInterval(() => {
      loadJobs();
      tick += 1;
      // refresh the DAG every ~5s regardless of jobs cadence
      if (interval >= 5000 || tick % 3 === 0) load({ quiet: true });
    }, interval);
    return () => clearInterval(t);
  }, [jobs.rows, loadJobs, load]);

  const runNow = useCallback(async () => {
    setRunning(true); setRunError(null);
    let started = false;
    // Optimistic: bump to page 0 + refresh jobs immediately so the new
    // in-progress row appears.
    setJobsPage(0);
    loadJobs();
    try {
      await runPipeline({ workflow_id: activeWf }); // run ONLY the active workflow
      started = true;
    } catch (e) {
      setRunError(e?.message || (typeof e === 'string' ? e : 'Pipeline run failed'));
    } finally {
      setRunning(false);
      loadJobs(); // final refresh to pick up the completed row
    }
    return started;
  }, [loadJobs, activeWf]);

  const nodeGraph = data.nodeGraph || { nodes: [], links: [] };
  const hasGraph = (nodeGraph.nodes && nodeGraph.nodes.length > 0) || (data.strategies && data.strategies.length > 0);

  // Workflow tabs now live in the canvas's IDE-style tab strip (and the
  // Workflows side-panel section) — no floating title card.

  // Floating action toolbar (top-right inside the canvas).
  const toolbarOverlay = (
    <div className="flex items-center gap-2">
      <button onClick={load} className="flex items-center gap-2 h-9 px-3 rounded-[6px] bg-card-bg/90 backdrop-blur text-sm text-page-text hover:bg-card-bg shadow-[0_4px_24px_0_var(--shadow-blue-md)]">
        <RefreshCw size={15}/> Refresh
      </button>
      <button
        onClick={() => setIsTargetChooserOpen(true)}
        className="flex items-center gap-2 h-9 px-3 rounded-[6px] bg-card-bg/90 backdrop-blur text-sm text-page-text hover:bg-card-bg shadow-[0_4px_24px_0_var(--shadow-blue-md)]"
        title="Add a data target (where the pipeline writes)"
      >
        <Plus size={15}/> Add target
      </button>
      {!isPortal() && (<button
        onClick={runNow}
        disabled={running || loading}
        data-tour="pipeline-run"
        className="flex items-center gap-2 h-9 px-3 rounded-[6px] bg-nav-active text-white text-sm hover:opacity-90 disabled:opacity-50 shadow-[0_4px_24px_0_var(--shadow-blue-md)]"
        title="Pin the current commit and run the pipeline: each transform node executes on the paired engine, in dependency order."
      >
        {running ? <Loader2 className="animate-spin" size={15}/> : <Play size={15}/>}
        {running ? 'Running…' : 'Run pipeline'}
      </button>)}
    </div>
  );

  // Floating banner overlay (loading / errors), top-center.
  const bannerOverlay = (loading || error || runError) ? (
    <div className="flex flex-col gap-2">
      {loading && (
        <div className="bg-card-bg/90 backdrop-blur text-page-text px-3 py-1.5 rounded-[6px] text-sm flex items-center gap-2 shadow-[0_4px_24px_0_var(--shadow-blue-md)]">
          <Loader2 className="animate-spin" size={15}/> Loading…
        </div>
      )}
      {error && <div className="bg-red-50 text-red-800 px-3 py-1.5 rounded-[6px] text-sm shadow">{error}</div>}
      {runError && (
        <div className="bg-red-50 text-red-800 px-3 py-1.5 rounded-[6px] text-sm flex items-start justify-between gap-3 shadow">
          <span><strong>Failed to start pipeline:</strong> {runError}</span>
          <button onClick={() => setRunError(null)} className="text-red-600 hover:bg-red-100 rounded p-0.5"><X size={14}/></button>
        </div>
      )}
    </div>
  ) : null;

  // Bottom-dock tabs. "Runs" holds the pipeline-runs table (rendered with the
  // dark/white embedded styling so it's readable on the dock).
  const dockTabs = [
    {
      id: "runs",
      label: "Runs",
      count: jobs.total || jobs.rows.length,
      content: (
        <JobsPanel
          jobs={jobs}
          page={jobsPage}
          perPage={JOBS_PER_PAGE}
          onPageChange={setJobsPage}
          onOpenJob={setOpenJobId}
          onRefresh={loadJobs}
          embedded
        />
      ),
    },
  ];

  return (
    <div data-tour="page-pipeline" className="h-full w-full flex bg-page-bg">
      {!railOpen && (
        <button type="button" onClick={toggleRail} title="Show pipelines list" data-testid="pipeline-rail-open"
          className="absolute z-20 mt-3 ml-2 text-page-muted hover:text-page-text bg-card-bg border border-card-border rounded-md cursor-pointer p-1.5">
          <PanelLeftOpen size={16} />
        </button>
      )}
      {/* Left rail: the pipelines explorer — same shell as the Apps/Agents/
          Workflows rails. Lists this project's pipeline tabs; the footer files
          a build-with-an-agent request. */}
      <aside data-tour="pipeline-rail" className={(railOpen ? "w-72" : "hidden") + " shrink-0 border-r border-card-border bg-card-bg/40 flex flex-col min-h-0"}>
        <div className="px-4 py-4 flex items-center gap-2 border-b border-card-border">
          <GitBranch size={18} className="text-nav-active" />
          <span className="font-semibold text-page-text">Pipeline</span>
          <button type="button" onClick={() => load()} title="Refresh" className="ml-auto text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1">
            <RefreshCw size={14} />
          </button>
          <button type="button" onClick={toggleRail} title="Collapse" data-testid="pipeline-rail-close" className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1">
            <PanelLeftClose size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          <div className="px-2.5 pt-1 pb-1.5 flex items-center text-[11px] font-semibold uppercase tracking-wide text-page-muted">
            My pipelines
            <button type="button" onClick={handleAddWorkflow} title="New pipeline" data-testid="pipeline-rail-add"
              className="ml-auto text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-0.5">
              <Plus size={13} />
            </button>
          </div>
          {(workflows || []).map((wf) => (
            <div key={wf.id} data-pipeline-rail-wf={wf.id}
              className={"group w-full flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm cursor-pointer " + (activeWf === wf.id ? "bg-nav-active/10 text-nav-active" : "text-page-text hover:bg-card-border/30")}
              onClick={() => setActiveWf(wf.id)}>
              <GitBranch size={13} className="shrink-0 opacity-60" />
              <span className="truncate flex-1">{wf.name}</span>
              <button type="button" title="Request a change to this pipeline"
                className="opacity-0 group-hover:opacity-100 text-page-muted hover:text-nav-active bg-transparent border-0 cursor-pointer p-0.5 shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  setBuildDialog({ preset: { kind: "change_pipeline", title: `Change "${wf.name}"`, details: `TARGET PIPELINE: ${wf.id} — ${wf.name}\n\n` } });
                }}>
                <Sparkles size={12} />
              </button>
            </div>
          ))}
          {!workflows?.length && <div className="text-xs text-page-muted px-2.5 py-2">No pipelines yet — build one below or drop nodes on the canvas.</div>}
        </div>
        {/* Rail footer: file a request for an agent-built pipeline. */}
        <div className="p-2 border-t border-card-border">
          <button type="button" data-testid="pipeline-request-new" onClick={() => setBuildDialog({ preset: { kind: "new_pipeline" } })}
            className="w-full inline-flex items-center justify-center gap-1.5 rounded-md text-sm px-3 py-2 border border-dashed border-card-border bg-transparent text-page-muted hover:text-page-text hover:bg-card-border/30 cursor-pointer">
            <Sparkles size={14} /> Build a pipeline
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 min-h-0 relative">
      {/* Full-page canvas: the node graph fills the content area; the
          node library + title + toolbar + banner + runs drawer all float inside
          it. A pipeline IS the typed strategy DAG over storages, drawn as a
          typed node graph (strategies = nodes with typed sockets; source/target
          storages = Load/Save nodes; intermediate storages = the typed links). */}
      <PipelineCanvas
        showWorkflowsPanel={false}
        project={projectSlug || null}
        nodeGraph={nodeGraph}
        storages={data.storages || []}
        strategies={data.strategies || []}
        kinds={strategyKinds}
        onClickStorage={(s) => {
          // Any entity-backed SOURCE or TARGET (file, scraper, enrichment, api,
          // slack, gmail, the SQL dialects, mongo, …) opens its full StorageDetail
          // page. Only engine-produced intermediates (parquet_raw/parquet_processed)
          // — which have no source page — use the lightweight inspector modal.
          const t = (s?.storage_type || '').toLowerCase();
          const INTERMEDIATE = t === 'parquet_raw' || t === 'parquet_processed';
          if (!INTERMEDIATE && s?.entity_id != null) {
            const origin = s.origin || ((s.entityKind === 'target' || s.direction === 'write') ? 'data_target' : 'data_source');
            navigate(createPageUrl(`storages/${origin}/${s.entity_id}`));
            return;
          }
          setActiveStorage(s);
        }}
        onClickStrategy={(m) => {
          // A node that declares a configuration app (params.config_view = a
          // *_editor.view endpoint on the project's own fork) opens THAT app in
          // a MODAL over the canvas — the configurable-endpoints pattern.
          // Config apps never appear on the Apps page. Everything else keeps
          // the inspector panel.
          const cv = m?.params?.config_view;
          if (typeof cv === "string" && /^[a-zA-Z0-9_.-]+\.view$/.test(cv)) {
            setConfigApp({ node: m.__node ? m : m, configView: cv });
            return;
          }
          setActiveStrategy(m);
        }}
        onCreateStrategy={handleCreateStrategy}
        onDeleteStrategy={handleDeleteStrategy}
        onDeleteNode={handleDeleteNode}
        catalog={catalog}
        onPickConnector={(c, position) => setConnectorForm({ connector: c, position })}
        existingSources={data.existingSources || []}
        onPlaceSource={handlePlaceSource}
        workflows={workflows}
        activeWorkflow={activeWf}
        onSelectWorkflow={setActiveWf}
        onAddWorkflow={handleAddWorkflow}
        onRenameWorkflow={handleRenameWorkflow}
        onDeleteWorkflow={handleDeleteWorkflow}
        toolbar={toolbarOverlay}
        banner={bannerOverlay}
        dockTabs={dockTabs}
      />
      {!loading && !hasGraph && (
        <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
          <div className="pointer-events-auto">
            <EmptyState hasEntities={data.sources.length > 0 || data.targets.length > 0} />
          </div>
        </div>
      )}
      </main>

      {buildDialog && (
        <AppRequestDialog
          kinds={PIPELINE_REQUEST_KINDS}
          preset={buildDialog.preset || {}}
          title="Build a pipeline"
          onClose={() => setBuildDialog(null)}
          onCreated={() => { setBuildDialog(null); setBuildFiled(true); }}
        />
      )}
      {buildFiled && (
        <div className="fixed bottom-5 right-5 z-50 rounded-lg border border-card-border bg-card-bg shadow-lg px-4 py-3 text-sm text-page-text flex items-center gap-3">
          <CheckCircle2 size={16} className="text-green-600" />
          <span>Pipeline request filed — an agent is on it. Track progress on the <b>Board</b>.</span>
          <button onClick={() => setBuildFiled(false)} className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1"><X size={14} /></button>
        </div>
      )}

      {connectorForm && (
        <ConnectorConfigModal
          connector={connectorForm.connector}
          onClose={() => setConnectorForm(null)}
          onSubmit={handleConnectorSubmit}
        />
      )}

      {viewEntity && (
        <ViewModal
          entity={viewEntity}
          onClose={() => setViewEntity(null)}
        />
      )}

      {activeConnection && (
        <ConnectionPanel
          connection={activeConnection}
          sources={data.sources}
          targets={data.targets}
          onClose={() => setActiveConnection(null)}
          onChanged={() => { load(); }}
        />
      )}

      {mapperState && (
        <FieldMapperModal
          source={mapperState.source}
          target={mapperState.target}
          onClose={() => setMapperState(null)}
          onSuccess={() => { setMapperState(null); load(); }}
        />
      )}

      {activeStorage && (
        <StorageInspectModal
          storage={activeStorage}
          onClose={() => setActiveStorage(null)}
        />
      )}

      {activeStrategy && (
        <StrategyInspectPanel
          strategy={activeStrategy}
          storages={data.storages || []}
          strategyKinds={strategyKinds}
          flowProjectId={flowProjectId}
          onClose={() => setActiveStrategy(null)}
        />
      )}

      {configApp && (
        <ConfigAppModal
          node={(() => {
            // Resolve the RAW node row for the clicked strategy, and derive the
            // upstream source / downstream target for the app's context hash: a
            // transform node has no own connector ids — they live in its
            // __domain/__codomain params ("<type>:<entityId>").
            const raw = (data.nodeGraph?.nodes || []).find((n) => n.id === configApp.node?.id) || configApp.node || {};
            let params = raw.params;
            if (typeof params === "string") { try { params = JSON.parse(params); } catch { params = {}; } }
            const ent = (v) => { const m = String((params || {})[v] || "").match(/^[a-z_]+:(\d+)$/i); return m ? Number(m[1]) : null; };
            // A collapsed GROUP stands for N parallel instances — the config app
            // must not scope to the first member's source; it shows its own
            // source selector when the hash carries none.
            const grouped = (configApp.node?.__groupCount || 0) > 1;
            return {
              ...raw,
              data_source_id: grouped ? null : (raw.data_source_id ?? ent("__domain")),
              data_target_id: raw.data_target_id ?? ent("__codomain"),
            };
          })()}
          configView={configApp.configView}
          forkProjectId={forkProjectId}
          project={projectSlug}
          onClose={() => setConfigApp(null)}
        />
      )}

      {openJobId && (
        <JobDetailModal
          jobId={openJobId}
          onClose={() => setOpenJobId(null)}
        />
      )}

      {isTargetChooserOpen && (
        <GuidedChooser
          title="Add a data target"
          manualLabel="Use the assistant"
          manualHint="Open the assistant chat and add the target yourself (e.g. ask it to call add_postgres_target)."
          guidedHint="Let a step-by-step guide register the target from a plain-language description and bind your data elements to it."
          onManual={() => { setIsTargetChooserOpen(false); window.dispatchEvent(new CustomEvent("cortex:open-harness", { detail: { harness: "orchestrator" } })); }}
          onGuided={() => { setIsTargetChooserOpen(false); startGuided("data_target"); }}
          onClose={() => setIsTargetChooserOpen(false)}
        />
      )}
    </div>
  );
}

// ─── Jobs panel + detail modal ─────────────────────────────────────────
function JobsPanel({ jobs, page, perPage, onPageChange, onOpenJob, onRefresh, embedded = false }) {
  const total = jobs.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  // In embedded (bottom-dock) mode the panel sits on the studio dark theme, so
  // force white text via --flow-text / --flow-text-muted (the cortex light-theme
  // page tokens would render near-black and unreadable here).
  const embStyle = embedded ? { color: "var(--flow-text)" } : undefined;
  const mutedCls = embedded ? "" : "text-page-muted";
  return (
    <div className={embedded ? "p-3" : "bg-card-bg rounded-[8px] p-4"} style={embStyle}>
      {!embedded && (
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-medium text-page-title">Pipeline runs</h2>
          <div className="flex items-center gap-2 text-sm text-page-muted">
            <span>{total} total</span>
            <button onClick={onRefresh} className="p-1 rounded hover:bg-page-bg" title="Refresh">
              <RefreshCw size={14}/>
            </button>
          </div>
        </div>
      )}
      {jobs.rows.length === 0 ? (
        <div className="text-sm py-4 text-center" style={embedded ? { color: "var(--flow-text-muted)" } : undefined}>No pipeline runs yet. Click "Run pipeline" to start one.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left" style={embedded ? { color: "var(--flow-text-muted)" } : undefined}>
            <tr className={`border-b ${embedded ? "border-white/15" : "border-page-divider"}`}>
              <th className="py-2 font-medium w-[34px]"></th>
              <th className="py-2 font-medium">Status</th>
              <th className="py-2 font-medium">Started</th>
              <th className="py-2 font-medium">Dolt commit</th>
              <th className="py-2 font-medium">Endpoint</th>
              <th className="py-2 font-medium">Rows written</th>
              <th className="py-2 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {jobs.rows.map(j => <JobRow key={j.id} job={j} embedded={embedded} onClick={() => onOpenJob(j.id)} />)}
          </tbody>
        </table>
      )}
      {totalPages > 1 && (
        <div className="flex items-center justify-end gap-2 mt-3 text-sm">
          <button
            disabled={page <= 0}
            onClick={() => onPageChange(p => Math.max(0, p - 1))}
            className="px-2 py-1 rounded border border-page-divider disabled:opacity-50"
          >Prev</button>
          <span className="text-page-muted">Page {page + 1} of {totalPages}</span>
          <button
            disabled={page >= totalPages - 1}
            onClick={() => onPageChange(p => p + 1)}
            className="px-2 py-1 rounded border border-page-divider disabled:opacity-50"
          >Next</button>
        </div>
      )}
    </div>
  );
}

function JobRow({ job, onClick, embedded = false }) {
  const mutedStyle = embedded ? { color: "var(--flow-text-muted)" } : undefined;
  const statusBadge = {
    in_progress: { color: "bg-blue-100 text-blue-700", icon: <Loader2 className="animate-spin" size={12}/>, label: "Running" },
    accepted:    { color: "bg-blue-100 text-blue-700", icon: <Clock size={12}/>, label: "Queued" },
    processing:  { color: "bg-blue-100 text-blue-700", icon: <Loader2 className="animate-spin" size={12}/>, label: "Processing" },
    completed:   { color: "bg-green-100 text-green-800", icon: <CheckCircle2 size={12}/>, label: "Completed" },
    failed:      { color: "bg-red-100 text-red-800", icon: <AlertCircle size={12}/>, label: "Failed" },
  }[job.status] || { color: "bg-gray-100 text-gray-700", icon: null, label: job.status };
  const fmt = (s) => s ? new Date(s).toLocaleString() : "—";
  const rowsSummary = job.rows_written
    ? Object.entries(job.rows_written).map(([k, v]) => `${k}=${v}`).join(', ')
    : (job.status === 'in_progress' ? '…' : '—');
  return (
    <tr onClick={onClick} className={`border-b last:border-b-0 cursor-pointer ${embedded ? "border-white/10 hover:bg-white/5" : "border-page-divider hover:bg-page-bg"}`}>
      <td className="py-2"></td>
      <td className="py-2">
        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${statusBadge.color}`}>
          {statusBadge.icon} {statusBadge.label}
        </span>
      </td>
      <td className={`py-2 ${embedded ? "" : "text-page-muted"}`} style={mutedStyle}>{fmt(job.created_at)}</td>
      <td className="py-2 font-mono text-xs">{job.dolt_commit ? job.dolt_commit.slice(0, 12) : '—'}</td>
      <td className="py-2 font-mono text-xs">{job.flow_endpoint || '—'}</td>
      <td className="py-2 text-xs">{rowsSummary}</td>
      <td className="py-2 text-right">
        <ChevronRight size={14} className={embedded ? "" : "text-page-muted"} style={mutedStyle}/>
      </td>
    </tr>
  );
}

function JobDetailModal({ jobId, onClose }) {
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    setLoading(true); setErr(null);
    getPipelineJob(jobId)
      .then(r => { if (alive) setJob(r); })
      .catch(e => { if (alive) setErr(e?.message || 'Failed to load job'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [jobId]);
  return (
    <ModalShell title={`Pipeline run ${jobId}`} onClose={onClose} wide>
      {loading ? (
        <div className="py-6 flex items-center justify-center text-page-muted"><Loader2 className="animate-spin mr-2" size={20}/> Loading…</div>
      ) : err ? (
        <div className="bg-red-50 text-red-800 px-4 py-2 rounded text-sm">{err}</div>
      ) : job ? (
        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <KV label="Status" value={job.status}/>
            <KV label="Project" value={job.cortex_project || '—'}/>
            <KV label="Started" value={job.created_at ? new Date(job.created_at).toLocaleString() : '—'}/>
            <KV label="Completed" value={job.completed_at ? new Date(job.completed_at).toLocaleString() : '—'}/>
            <KV label="Dolt commit" value={<span className="font-mono text-xs">{job.payload?.cortex_dolt_commit || '—'}</span>}/>
            <KV label="Flow endpoint" value={<span className="font-mono text-xs">{job.payload?.flow_endpoint || '—'}</span>}/>
          </div>
          {job.error_message && (
            <div className="bg-red-50 text-red-800 px-3 py-2 rounded text-sm">
              <div className="font-medium mb-1">Error</div>
              <pre className="whitespace-pre-wrap text-xs">{job.error_message}</pre>
            </div>
          )}
          {job.result?.rows_written && (
            <div>
              <div className="font-medium text-page-title mb-1">Rows written</div>
              <table className="w-full text-sm">
                <tbody>
                  {Object.entries(job.result.rows_written).map(([k, v]) => (
                    <tr key={k} className="border-b border-page-divider last:border-b-0">
                      <td className="py-1 font-mono text-xs">{k}</td>
                      <td className="py-1 text-right font-mono">{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <details>
            <summary className="cursor-pointer text-page-muted text-xs">Raw payload</summary>
            <pre className="bg-page-bg p-2 rounded text-xs overflow-auto max-h-80 mt-2">{JSON.stringify(job.payload, null, 2)}</pre>
          </details>
          {job.result && (
            <details>
              <summary className="cursor-pointer text-page-muted text-xs">Full Flow result</summary>
              <pre className="bg-page-bg p-2 rounded text-xs overflow-auto max-h-80 mt-2">{JSON.stringify(job.result, null, 2)}</pre>
            </details>
          )}
        </div>
      ) : null}
    </ModalShell>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="text-xs text-page-muted">{label}</div>
      <div className="text-sm">{value}</div>
    </div>
  );
}

// ─── Storage / strategy inspect (DAG node + edge clicks) ───────────────
//
// The /pipeline view is a typed-storage DAG. Clicking a STORAGE node opens
// this modal (the storage analogue of the old source/target ViewModal):
//   - file              → a data_source: live Schema + Records via source API
//   - postgres          → a data_target: live Schema + Records via target API
//   - parquet_raw/_proc  → engine artifacts: schema comes from the storage row's
//                          schema_json (no live record endpoint), so Records is
//                          hidden and Schema renders the carried shape.
const STORAGE_META = {
  file:              { label: "File",                icon: FileText, color: "#21A366", entityKind: "source" },
  parquet_raw:       { label: "Parquet (raw)",       icon: Boxes,    color: "#8B5CF6", entityKind: "parquet_raw" },
  parquet_processed: { label: "Parquet (processed)", icon: Boxes,    color: "#6366F1", entityKind: "parquet_processed" },
  postgres:          { label: "Postgres",            icon: Database, color: "#377AD6", entityKind: "target" },
  mongo:             { label: "Mongo",               icon: Server,   color: "#13AA52", entityKind: "target" },
};
const storageMeta = (t) => STORAGE_META[t] || { label: t || "Storage", icon: Server, color: "#94a3b8", entityKind: null };

// Normalize a storage's carried schema_json into the {tables:[{name,columns}]}
// shape SchemaView expects. parquet_processed carries {canonical_elements:[]};
// other artifacts may carry {columns:[]} or {fields:[]}.
function storageSchemaToTables(storage) {
  const sj = storage.schema_json;
  if (!sj || typeof sj !== "object") return null;
  if (Array.isArray(sj.tables)) return { tables: sj.tables };
  if (Array.isArray(sj.canonical_elements)) {
    return {
      tables: [{
        name: storage.name || "processed",
        columns: sj.canonical_elements.map((e) => ({
          name: e.target_column || e.canonical_name,
          type: e.type || "—",
          nullable: true,
          canonical_name: e.canonical_name,
        })),
      }],
    };
  }
  const cols = sj.columns || sj.fields;
  if (Array.isArray(cols)) {
    return {
      tables: [{
        name: storage.name || storage.storage_type,
        columns: cols.map((c) => ({ name: c.name || c, type: c.type || "—", nullable: c.nullable ?? true })),
      }],
    };
  }
  return null;
}

// Connection-config editor for a db storage (the Connection tab). Reads/writes
// the storage's `config` JSON via /api/storage_config. Field set is kind-aware:
// postgres → host/port/database/user/password/schema; mongo → database/collection.
const CONN_FIELDS = {
  postgres: [
    { key: "host", label: "Host" }, { key: "port", label: "Port", type: "number" },
    { key: "database", label: "Database" }, { key: "schema", label: "Schema" },
    { key: "user", label: "User" }, { key: "password", label: "Password", type: "password" },
  ],
  mongo: [
    { key: "uri", label: "Connection URI", placeholder: "mongodb://host:27017/?replicaSet=rs0" },
    { key: "database", label: "Database" },
    // Collection is NOT a connection parameter — each data element's target
    // collection is set per-mapping in the write Mappings (Target collection).
  ],
};
// Non-connection fields that live in a connector's config_schema but aren't
// part of "how to connect" (they're naming / file-upload concerns).
const NON_CONN_FIELD = (f) => f.type === 'file' || ['source_name', 'target_name', 'file'].includes(f.name);

export function ConnectionTab({ origin, entityId, storageType }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [saved, setSaved] = useState(false);
  // Connection fields are DATA-DRIVEN from the node catalog's connector
  // config_schema for this storage type — so every source type (mysql, oracle,
  // snowflake, bigquery, redshift, s3, …) shows its own fields, not just pg/mongo.
  // Falls back to the static CONN_FIELDS map if the catalog can't be reached.
  const [fields, setFields] = useState(null);

  useEffect(() => {
    let alive = true;
    getNodeCatalog().then(cat => {
      if (!alive) return;
      const conns = cat?.connectors || [];
      const role = (origin === 'data_target') ? 'save' : 'load';
      const conn = conns.find(c => (c.storage_type_out === storageType && c.role === 'load'))
        || conns.find(c => (c.storage_type_in === storageType && c.role === 'save'))
        || conns.find(c => c.storage_type_out === storageType || c.storage_type_in === storageType);
      const schemaFields = conn?.config_schema?.fields || [];
      const mapped = schemaFields.filter(f => !NON_CONN_FIELD(f)).map(f => ({ key: f.name, label: f.label || f.name, type: f.type, placeholder: f.placeholder }));
      setFields(mapped.length ? mapped : (CONN_FIELDS[storageType] || []));
    }).catch(() => { if (alive) setFields(CONN_FIELDS[storageType] || []); });
    return () => { alive = false; };
  }, [storageType, origin]);

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr(null);
    getStorageConfig(origin, entityId)
      .then(r => { if (alive) setConfig(r?.config || {}); })
      .catch(e => { if (alive) setErr(e?.message || "Failed to load connection"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [origin, entityId]);

  const save = async () => {
    setSaving(true); setErr(null); setSaved(false);
    try {
      const out = { ...config };
      // Coerce numeric fields (port, etc.) per the field schema.
      for (const f of (fields || [])) {
        if (f.type === 'number' && out[f.key] != null && out[f.key] !== '') out[f.key] = Number(out[f.key]);
      }
      await updateStorageConfig(origin, entityId, out);
      setSaved(true); setTimeout(() => setSaved(false), 2500);
    } catch (e) { setErr(e?.message || "Failed to save connection"); }
    finally { setSaving(false); }
  };

  if (loading || fields == null) return <div className="text-page-muted text-sm"><Loader2 className="inline animate-spin mr-2" size={14}/>Loading connection…</div>;
  if (err && !config) return <div className="text-red-700 text-sm">{err}</div>;
  // Stacked-label form: label (+ optional helper) ABOVE a full-width control, so
  // long labels/placeholders never squeeze into a cramped left column. textarea
  // fields span the full width; short fields sit in a responsive 2-col grid.
  const fieldInput = (f) => f.type === 'checkbox' ? (
    // Real boolean control — a text input here captured the flag as a string.
    <input
      type="checkbox"
      checked={Boolean(config?.[f.key]) && config?.[f.key] !== 'false'}
      onChange={e => setConfig(c => ({ ...c, [f.key]: e.target.checked }))}
      className="h-4 w-4 accent-[var(--nav-active,#377AD6)]"
    />
  ) : f.type === 'textarea' ? (
    <textarea
      placeholder={f.placeholder || ""}
      value={config?.[f.key] ?? ""}
      onChange={e => setConfig(c => ({ ...c, [f.key]: e.target.value }))}
      className="w-full px-3 py-2 text-sm rounded-md border border-page-divider bg-card-bg leading-relaxed min-h-[120px] focus:outline-none focus:ring-2 focus:ring-nav-active/30 focus:border-nav-active transition"
    />
  ) : (
    <input
      type={f.type === 'number' ? 'number' : (f.type === 'password' ? 'password' : 'text')}
      placeholder={f.placeholder || ""}
      value={config?.[f.key] ?? ""}
      onChange={e => setConfig(c => ({ ...c, [f.key]: e.target.value }))}
      className="w-full h-9 px-3 text-sm rounded-md border border-page-divider bg-card-bg focus:outline-none focus:ring-2 focus:ring-nav-active/30 focus:border-nav-active transition"
    />
  );
  // Split label "Name (helper text)" → bold label + muted helper line.
  const splitLabel = (label) => { const m = /^([^(]+?)\s*\(([^)]*)\)\s*$/.exec(label || ""); return m ? { main: m[1].trim(), help: m[2].trim() } : { main: label, help: null }; };
  const wide = fields.filter(f => f.type === 'textarea');
  const narrow = fields.filter(f => f.type !== 'textarea');
  // Plain render FUNCTION, not a component: a component type defined inside
  // this component's body gets a new identity on every render, so React
  // remounted the whole field subtree per keystroke and the input lost focus
  // after each character. A function call keeps the element types stable.
  const renderField = (f) => { const { main, help } = splitLabel(f.label); return (
    <div key={f.key} className="space-y-1">
      <label className="block text-sm font-medium text-page-text">{main}</label>
      {help && <div className="text-xs text-page-muted leading-snug">{help}</div>}
      {fieldInput(f)}
    </div>
  ); };
  return (
    <div className="space-y-5 max-w-2xl">
      {fields.length === 0 && <div className="text-sm text-page-muted">This source type has no connection settings.</div>}
      {narrow.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
          {narrow.map(f => renderField(f))}
        </div>
      )}
      {wide.map(f => renderField(f))}
      <div className="flex items-center gap-3 pt-1">
        <button onClick={save} disabled={saving}
          className="inline-flex items-center h-9 px-4 text-sm font-medium rounded-md bg-nav-active text-white hover:bg-nav-active-dark disabled:opacity-50 transition-colors">
          {saving ? "Saving…" : "Save connection"}
        </button>
        {saved && <span className="text-xs text-green-600 font-medium">Saved ✓</span>}
        {err && <span className="text-xs text-red-700">{err}</span>}
      </div>
    </div>
  );
}

// Unified mappings surface for a db storage. READ direction = storage field →
// data_element (like the file source page). WRITE direction = data_element →
// target_table/target_column (collection for mongo). Both are source_field_mapping
// rows keyed by storage_id + direction.
export function StorageMappingsTab({ storageId, storageType, writeDirection }) {
  const direction = writeDirection ? "write" : "read";
  const [rows, setRows] = useState(null);
  const [elements, setElements] = useState([]);
  const [err, setErr] = useState(null);
  const [draft, setDraft] = useState({ canonical_element_id: "", source_field_name: "", target_table: "", target_column: "", fan_out_by_source_field: false });

  const load = useCallback(async () => {
    setErr(null);
    try {
      const r = await listStorageMappings(storageId, direction);
      setRows(r?.data || r || []);
    } catch (e) { setErr(e?.message || "Failed to load mappings"); }
  }, [storageId, direction]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { DataElementView.list("canonical_name").then(d => setElements(d || [])).catch(() => {}); }, []);

  const add = async () => {
    setErr(null);
    try {
      const body = {
        storage_id: storageId, storage_type: storageType, direction,
        canonical_element_id: draft.canonical_element_id || null,
      };
      if (writeDirection) {
        body.target_table = draft.target_table; body.target_column = draft.target_column;
        body.fan_out_by_source_field = draft.fan_out_by_source_field ? 1 : 0;
      } else {
        body.source_field_name = draft.source_field_name;
      }
      await SourceFieldMapping.create(body);
      setDraft({ canonical_element_id: "", source_field_name: "", target_table: "", target_column: "", fan_out_by_source_field: false });
      await load();
    } catch (e) { setErr(e?.message || "Failed to add mapping"); }
  };
  const remove = async (id) => { try { await SourceFieldMapping.delete(id); await load(); } catch (e) { setErr(e?.message || "Failed"); } };

  const elName = (id) => elements.find(e => String(e.id) === String(id))?.canonical_name || id;
  // Mongo speaks collections + document fields, not tables/columns. The data
  // model still uses target_table/target_column (engine contract), but the
  // labels must match the storage's own vocabulary.
  const isMongo = storageType === "mongo";
  const tableLabel = isMongo ? "Target collection" : "Target table";
  const colLabel = isMongo ? "Target field" : "Target column";
  const tablePlaceholder = isMongo ? "collection" : "table";
  const colPlaceholder = isMongo ? "field" : "column";
  if (rows == null) return <div className="text-page-muted text-sm"><Loader2 className="inline animate-spin mr-2" size={14}/>Loading mappings…</div>;
  return (
    <div className="space-y-3">
      <div className="text-xs text-page-muted">
        {writeDirection
          ? (isMongo
              ? "WRITE mappings — which data element lands in which collection / document field."
              : "WRITE mappings — which data element lands in which target table / column.")
          : "READ mappings — which field of this source maps to which canonical data element."}
      </div>
      <table className="w-full text-sm">
        <thead><tr className="text-left text-page-muted border-b border-page-divider">
          {writeDirection
            ? (<><th className="py-1.5">Data element</th><th>{tableLabel}</th><th>{colLabel}</th><th>Fan-out</th><th/></>)
            : (<><th className="py-1.5">Source field</th><th>Data element</th><th/></>)}
        </tr></thead>
        <tbody>
          {rows.map(m => (
            <tr key={m.id} className="border-b border-page-divider/50">
              {writeDirection ? (<>
                <td className="py-1.5">{m.element_canonical_name || elName(m.canonical_element_id)}</td>
                <td className="font-mono text-xs">{m.target_table}</td>
                <td className="font-mono text-xs">{m.target_column}</td>
                <td>{m.fan_out_by_source_field ? "yes" : ""}</td>
              </>) : (<>
                <td className="py-1.5 font-mono text-xs">{m.source_field_name}</td>
                <td>{m.element_canonical_name || elName(m.canonical_element_id)}</td>
              </>)}
              <td className="text-right"><button onClick={() => remove(m.id)} className="text-page-muted hover:text-red-600"><Trash2 size={14}/></button></td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={5} className="py-3 text-page-muted text-center">No {direction} mappings yet.</td></tr>}
        </tbody>
      </table>
      {/* Add row */}
      <div className="flex items-center gap-2 pt-1 flex-wrap">
        {!writeDirection && (
          <input placeholder="source field" value={draft.source_field_name}
            onChange={e => setDraft(d => ({ ...d, source_field_name: e.target.value }))}
            className="px-2 py-1.5 text-sm rounded border border-page-divider bg-card-bg font-mono w-40"/>
        )}
        <select value={draft.canonical_element_id}
          onChange={e => setDraft(d => ({ ...d, canonical_element_id: e.target.value }))}
          className="px-2 py-1.5 text-sm rounded border border-page-divider bg-card-bg">
          <option value="">data element…</option>
          {elements.map(e => <option key={e.id} value={e.id}>{e.canonical_name}</option>)}
        </select>
        {writeDirection && (<>
          <input placeholder={tablePlaceholder} value={draft.target_table}
            onChange={e => setDraft(d => ({ ...d, target_table: e.target.value }))}
            className="px-2 py-1.5 text-sm rounded border border-page-divider bg-card-bg font-mono w-32"/>
          <input placeholder={colPlaceholder} value={draft.target_column}
            onChange={e => setDraft(d => ({ ...d, target_column: e.target.value }))}
            className="px-2 py-1.5 text-sm rounded border border-page-divider bg-card-bg font-mono w-32"/>
          <label className="text-xs text-page-muted flex items-center gap-1">
            <input type="checkbox" checked={draft.fan_out_by_source_field}
              onChange={e => setDraft(d => ({ ...d, fan_out_by_source_field: e.target.checked }))}/> fan-out
          </label>
        </>)}
        <button onClick={add} className="px-3 py-1.5 text-sm rounded bg-nav-active text-white">Add</button>
      </div>
      {err && <div className="text-xs text-red-700">{err}</div>}
    </div>
  );
}

export function StorageInspectModal({ storage, onClose }) {
  const meta = storageMeta(storage.storage_type);
  const Icon = meta.icon;
  // 'source' | 'target' | 'parquet_raw' | 'parquet_processed' | null.
  // When the caller knows which table the row lives in (storage.origin), that
  // WINS over the type-map guess: a FILE data_target must inspect as a target
  // (write direction), and kinds STORAGE_META doesn't know (mysql, s3, rss, …)
  // still get working Schema/Records tabs via their origin.
  const isParquetType = meta.entityKind === "parquet_raw" || meta.entityKind === "parquet_processed";
  const entityKind = (!isParquetType && storage.origin === "data_target") ? "target"
    : (!isParquetType && storage.origin === "data_source") ? "source"
    : meta.entityKind;
  const isParquet = entityKind === "parquet_raw" || entityKind === "parquet_processed";
  // Live = has a record endpoint. File/postgres also have a live SCHEMA endpoint;
  // parquet carries its schema on the storage row but DOES have a live records reader.
  const hasLiveSchema = entityKind === "source" || entityKind === "target";
  const live = !!entityKind;
  const entityId = storage.entity_id;

  // Storages backed by a data_source/data_target row get Connection + Mappings
  // config tabs (the config route is kind-generic). `origin` selects the
  // underlying table (write storages are data_target; read storages data_source).
  const isDbStorage = hasLiveSchema && storage.storage_type !== "file";
  const origin = storage.origin || (entityKind === "target" ? "data_target" : "data_source");
  const writeMappings = entityKind === "target"; // codomain storages bind elements → table/col

  const [tab, setTab] = useState("overview");
  const [schemaData, setSchemaData] = useState(null);
  const [schemaErr, setSchemaErr] = useState(null);
  const [schemaLoading, setSchemaLoading] = useState(false);

  const loadSchema = useCallback(async () => {
    setSchemaLoading(true); setSchemaErr(null);
    try {
      if (hasLiveSchema) {
        const data = entityKind === "source"
          ? await getSourceSchema(entityId)
          : await getTargetSchema(entityId);
        setSchemaData(data);
      } else {
        // Parquet / engine artifact — use the schema carried on the storage row.
        const tables = storageSchemaToTables(storage);
        setSchemaData(tables || { tables: [], note: "No schema recorded for this node yet." });
      }
    } catch (e) {
      setSchemaErr(e?.message || "Failed to load schema");
    } finally {
      setSchemaLoading(false);
    }
  }, [hasLiveSchema, entityKind, entityId, storage]);

  useEffect(() => {
    if (tab === "schema" && !schemaData && !schemaLoading && !schemaErr) loadSchema();
  }, [tab, schemaData, schemaLoading, schemaErr, loadSchema]);

  const tabs = [
    { id: "overview", label: "Overview" },
    ...(isDbStorage ? [{ id: "connection", label: "Connection" }] : []),
    { id: "schema", label: "Schema" },
    ...(isDbStorage ? [{ id: "mappings", label: "Mappings" }] : []),
    ...(live ? [{ id: "records", label: "Records" }] : []),
  ];

  return (
    <ModalShell title={`Data point: ${storage.name}`} onClose={onClose} wide>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-[6px] flex items-center justify-center" style={{ background: meta.color + "20", color: meta.color }}>
          <Icon size={20}/>
        </div>
        <div>
          <div className="font-medium text-sm">{meta.label}</div>
          <div className="text-xs text-page-muted font-mono">{storage.storage_id}</div>
        </div>
        <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800">{storage.status || "Active"}</span>
      </div>

      <div className="flex items-center gap-1 border-b border-page-divider mb-4">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${tab === t.id ? "border-nav-active text-nav-active" : "border-transparent text-page-muted hover:text-page-text"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <dl className="grid grid-cols-2 gap-y-3 text-sm">
          <dt className="text-page-muted">Name</dt><dd className="font-medium">{storage.name}</dd>
          <dt className="text-page-muted">Data point type</dt><dd className="font-mono text-xs">{storage.storage_type}</dd>
          {storage.adapter_kind && (<><dt className="text-page-muted">Adapter</dt><dd className="font-mono text-xs">{storage.adapter_kind}</dd></>)}
          <dt className="text-page-muted">Status</dt><dd>{storage.status || "Active"}</dd>
          {isParquet && <dd className="col-span-2 text-xs text-page-muted pt-1">Engine artifact (pipeline parquet). Schema is what the pipeline produces; the Records tab reads the latest parquet rows from S3.</dd>}
        </dl>
      )}
      {tab === "schema" && (
        <div>
          {schemaLoading ? (
            <div className="text-page-muted text-sm"><Loader2 className="inline animate-spin mr-2" size={14}/>Discovering schema…</div>
          ) : schemaErr ? (
            <div className="text-red-700 text-sm">{schemaErr}</div>
          ) : !schemaData?.tables?.length ? (
            <div className="text-page-muted text-sm">{schemaData?.note || "No tables."}</div>
          ) : (
            <SchemaView schemaData={schemaData} isSource={entityKind === "source"} />
          )}
        </div>
      )}
      {tab === "connection" && isDbStorage && (
        <ConnectionTab origin={origin} entityId={entityId} storageType={storage.storage_type} />
      )}
      {tab === "mappings" && isDbStorage && (
        <StorageMappingsTab storageId={entityId} storageType={storage.storage_type} writeDirection={writeMappings} />
      )}
      {tab === "records" && live && (
        <RecordsTab kind={entityKind} entityId={entityId} schemaData={schemaData} loadSchema={loadSchema} schemaLoading={schemaLoading} schemaErr={schemaErr} />
      )}
    </ModalShell>
  );
}

// Strategy edge click → what this typed arrow does: kind, domain→codomain,
// and its params (rule names for processing_rules, mapping/table info for
// relational_load, etc.).
function StrategyInspectPanel({ strategy, storages, strategyKinds, flowProjectId, onClose }) {
  const byId = useMemo(() => new Map((storages || []).map(s => [s.storage_id, s])), [storages]);
  const dom = byId.get(strategy.domain_storage_id);
  const cod = byId.get(strategy.codomain_storage_id);
  const params = strategy.params && typeof strategy.params === "object" ? strategy.params : null;
  const KIND_LABEL = {
    file_to_parquet:  "File → Parquet (internal)",
    processing_rules: "Processing rules",
    deduplication:    "Deduplication",
    enrichment:       "Enrichment",
    relational_load:  "Stream into Postgres",
    mongo_load:       "Stream into Mongo",
    db_to_db:         "Stream into Postgres (copy)",
    pg_to_mongo:      "Stream into Mongo (copy)",
  };
  // The strategy kind is implemented by a Flow endpoint in the prototype project.
  // Embed that endpoint's live Flow canvas in the modal + offer a full-page link.
  const kindDef = (strategyKinds || []).find(k => k.kind === strategy.kind);
  const impl = kindDef?.impl_endpoint || kindDef?.flow_endpoint;
  // Full studio link (Open in Flow) vs. the bare embed route for the iframe
  // (no cortex sidebar, no studio toolbar — only the endpoint diagram).
  const flowHref = flowProjectId && impl
    ? `/flow/projects/${flowProjectId}/endpoints/${impl}/flow`
    : null;
  const embedHref = flowProjectId && impl
    ? `/flow-embed/projects/${flowProjectId}/endpoints/${impl}/flow`
    : null;
  // Node-native click passes a thin {id, kind, __node, inType, outType} — prefer
  // those; fall back to the storage projection (domain/codomain) for the legacy
  // graph. Show input/output PORT TYPES (node vocabulary), not domain/codomain.
  const inType = strategy.inType || dom?.storage_type || null;
  const outType = strategy.outType || cod?.storage_type || null;
  return (
    <ModalShell title={`Strategy: ${KIND_LABEL[strategy.kind] || strategy.kind}`} onClose={onClose} wide>
      {(inType || outType) && (
        <div className="flex items-center gap-2 text-sm mb-4">
          <span className="font-mono px-2 py-1 rounded bg-page-bg">{inType || "—"}</span>
          <ArrowRight size={16} className="text-page-muted"/>
          <span className="font-mono px-2 py-1 rounded bg-page-bg">{outType || "—"}</span>
        </div>
      )}
      <dl className="grid grid-cols-2 gap-y-3 text-sm mb-4">
        <dt className="text-page-muted">Type</dt><dd className="font-mono text-xs">{strategy.kind}</dd>
        <dt className="text-page-muted">Input</dt><dd className="font-mono text-xs">{inType || "—"}</dd>
        <dt className="text-page-muted">Output</dt><dd className="font-mono text-xs">{outType || "—"}</dd>
      </dl>

      {/* Implementation: the live Flow endpoint canvas, embedded. The iframe
          loads the same-origin /flow route (shares the cortex token, so it
          auto-authenticates), plus an "Open in Flow" link for the full studio. */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <div className="text-xs font-semibold text-page-muted uppercase">Implementation in Flow</div>
          {flowHref && (
            <Link to={flowHref} data-testid="strategy-open-in-flow"
              className="inline-flex items-center gap-1.5 text-xs text-nav-active hover:text-nav-active-hover no-underline">
              <ExternalLink size={13} /> Open in Flow
              <code className="text-[11px] text-page-muted ml-1">{impl}</code>
            </Link>
          )}
        </div>
        {embedHref ? (
          <iframe
            data-testid="strategy-flow-iframe"
            src={embedHref}
            title={`Flow implementation: ${impl}`}
            className="w-full rounded-[8px] border border-card-border bg-page-bg"
            style={{ height: 420 }}
          />
        ) : (
          <div className="text-page-muted text-sm">No Flow implementation linked for this kind.</div>
        )}
      </div>

      <div>
        <div className="text-xs font-semibold text-page-muted uppercase mb-1">Configuration</div>
        <StrategyParamsEditor strategy={strategy} kindDef={kindDef}
                              kindLabel={KIND_LABEL[strategy.kind] || strategy.kind} />
      </div>
    </ModalShell>
  );
}

// Editable node configuration, driven by the kind's params_schema — the form
// the registry row always promised ("params_schema drives the node's in-canvas
// form") but nothing rendered until now. Schema-driven so EVERY kind gets a
// form from its registry row alone, with no per-kind component and no frontend
// deploy; a kind without a schema falls back to showing stored params as JSON.
// Saves via the node API for node-native graphs (strategy.__node) and the
// strategy API for the legacy projection — both PUTs accept {params}.
function StrategyParamsEditor({ strategy, kindDef, kindLabel }) {
  const schemaFields = useMemo(() => {
    try {
      const s = typeof kindDef?.params_schema === "string"
        ? JSON.parse(kindDef.params_schema) : kindDef?.params_schema;
      return Array.isArray(s?.fields) ? s.fields : [];
    } catch { return []; }
  }, [kindDef]);
  const stored = strategy.params && typeof strategy.params === "object" ? strategy.params : {};
  const [values, setValues] = useState(() => {
    const v = {};
    for (const f of schemaFields) v[f.name] = stored[f.name] ?? "";
    return v;
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState(null);

  if (!schemaFields.length) {
    return stored && Object.keys(stored).length ? (
      <pre className="bg-page-bg p-2 rounded text-xs overflow-auto max-h-80">{JSON.stringify(stored, null, 2)}</pre>
    ) : (
      <div className="text-page-muted text-sm">No configuration — this node runs the engine's standard {kindLabel} transform.</div>
    );
  }

  const inputCls = "w-full rounded-md border border-card-border bg-page-bg text-page-text text-xs px-2.5 py-1.5 outline-none focus:border-nav-active font-mono";
  const missing = schemaFields.filter((f) => f.required && !String(values[f.name] ?? "").trim());

  async function save() {
    setSaving(true); setErr(null); setSaved(false);
    try {
      // Merge over stored params so keys outside the schema (engine-internal
      // "__"-prefixed bookkeeping) survive the edit.
      const merged = { ...stored };
      for (const f of schemaFields) {
        const raw = String(values[f.name] ?? "").trim();
        if (raw === "") delete merged[f.name];
        else merged[f.name] = raw;
      }
      if (strategy.__node) await updateNode(strategy.id, { params: merged });
      else await updateStrategy(strategy.id, merged);
      setSaved(true);
    } catch (e) {
      setErr(String(e?.message || e));
    } finally { setSaving(false); }
  }

  return (
    <div className="grid gap-2" data-testid="strategy-params-editor">
      {schemaFields.map((f) => (
        <label key={f.name} className="block">
          <span className="block text-[11px] text-page-muted mb-0.5">
            {f.label || f.name}{f.required ? " *" : ""}
          </span>
          {f.type === "textarea" ? (
            <textarea className={inputCls} rows={4} value={values[f.name] ?? ""} spellCheck={false}
                      placeholder={f.placeholder || ""} data-param-field={f.name}
                      onChange={(e) => { setSaved(false); setValues((v) => ({ ...v, [f.name]: e.target.value })); }} />
          ) : (
            <input className={inputCls} value={values[f.name] ?? ""} spellCheck={false}
                   placeholder={f.placeholder || ""} data-param-field={f.name}
                   onChange={(e) => { setSaved(false); setValues((v) => ({ ...v, [f.name]: e.target.value })); }} />
          )}
        </label>
      ))}
      <div className="flex items-center gap-2">
        <button type="button" data-testid="strategy-params-save"
                className="inline-flex items-center gap-1.5 rounded-md bg-nav-active text-white text-xs px-3 py-1.5 border-0 cursor-pointer disabled:opacity-50"
                onClick={save} disabled={saving || missing.length > 0}
                title={missing.length ? `Required: ${missing.map((f) => f.label || f.name).join(", ")}` : ""}>
          {saving ? <Loader2 size={12} className="animate-spin" /> : null}
          Save configuration
        </button>
        {saved && <span className="text-xs text-emerald-600" data-testid="strategy-params-saved">Saved.</span>}
        {missing.length > 0 && <span className="text-[11px] text-page-muted">Required: {missing.map((f) => f.label || f.name).join(", ")}</span>}
        {err && <span className="text-xs text-red-500">{err}</span>}
      </div>
    </div>
  );
}

function EmptyState({ hasEntities = false }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center p-12 gap-3">
      <Settings2 size={32} className="text-page-muted"/>
      <div className="text-page-muted">
        {hasEntities
          ? 'No transformations wired yet — this pipeline has no DAG.'
          : 'No data points or transformations configured yet.'}
      </div>
      <div className="text-sm text-page-muted max-w-md">
        {hasEntities
          ? 'A pipeline is a DAG of typed data points connected by typed transformations. Ask the Cortex agent to wire the transformations (e.g. processing rules → relational/mongo load) so the pipeline can run.'
          : 'Ask the Cortex agent to set up your project — it can add a source data point, a target, propose mappings, create processing rules, and wire the transformation DAG.'}
      </div>
    </div>
  );
}

// ─── Two-column canvas with SVG connection layer ────────────────────────
//
// UI design notes:
//   - Edges are rendered as thin curves only. No mid-line badges — with
//     many sources fanning into one target the badges stacked into a
//     vertical pile and obscured each other.
//   - Each source card shows a compact "binds to →" row listing the
//     target_table·count pairs locally. Hovering a chip OR a source card
//     highlights the matching curve(s). Clicking a chip opens the
//     connection panel.
//   - Hovering a curve also highlights itself + the source/target cards.
function TwoColumnCanvas({ sources, targets, connections, onViewSource, onViewTarget, onClickConnection, onDragConnect }) {
  const canvasRef = useRef(null);
  const cardRefs = useRef({ source: new Map(), target: new Map() });
  const [positions, setPositions] = useState({}); // cardKey -> {x,y,w,h}
  const [dragLine, setDragLine] = useState(null); // {x1,y1,x2,y2,sourceId?}
  const [hoveredEdgeKey, setHoveredEdgeKey] = useState(null); // `${source_id}:${target_id}:${target_table}`
  const [hoveredSourceId, setHoveredSourceId] = useState(null);

  // Measure card positions whenever layout changes.
  const measure = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const canvasRect = canvas.getBoundingClientRect();
    const next = {};
    for (const kind of ['source', 'target']) {
      for (const [id, el] of cardRefs.current[kind].entries()) {
        if (!el) continue;
        const r = el.getBoundingClientRect();
        next[`${kind}:${id}`] = {
          x: r.left - canvasRect.left,
          y: r.top - canvasRect.top,
          w: r.width,
          h: r.height,
        };
      }
    }
    setPositions(next);
  }, []);

  useEffect(() => {
    measure();
    const ro = new ResizeObserver(measure);
    if (canvasRef.current) ro.observe(canvasRef.current);
    window.addEventListener('resize', measure);
    return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
  }, [measure, sources.length, targets.length, connections.length]);

  // Drag-to-connect from a source's right edge.
  const onSourceDragStart = (sourceId, e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const startPos = positions[`source:${sourceId}`];
    if (!startPos) return;
    const x1 = startPos.x + startPos.w;
    const y1 = startPos.y + startPos.h / 2;
    const move = (ev) => {
      setDragLine({ x1, y1, x2: ev.clientX - rect.left, y2: ev.clientY - rect.top, sourceId });
    };
    const up = (ev) => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      // Hit test against target cards
      const dropX = ev.clientX, dropY = ev.clientY;
      let droppedTarget = null;
      for (const [id, el] of cardRefs.current.target.entries()) {
        const r = el.getBoundingClientRect();
        if (dropX >= r.left && dropX <= r.right && dropY >= r.top && dropY <= r.bottom) {
          droppedTarget = targets.find(t => t.id === id);
          break;
        }
      }
      setDragLine(null);
      if (droppedTarget) {
        const source = sources.find(s => s.id === sourceId);
        onDragConnect(source, droppedTarget);
      }
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  return (
    <div ref={canvasRef} className="relative flex-1 grid grid-cols-2 gap-x-32 gap-y-3 min-h-[500px] p-4">
      {/* SVG overlay for connection lines — curves only, labels live on
          the source cards as chips */}
      <svg className="absolute inset-0 pointer-events-none" style={{ width: '100%', height: '100%' }}>
        {(() => {
          // Group connections by (source_id, target_id) for fan-stacking, so
          // multiple bound tables between the same source/target render as
          // parallel curves rather than overlapping.
          const grouped = {};
          for (const c of connections) {
            const k = `${c.source_id}:${c.target_id}`;
            (grouped[k] = grouped[k] || []).push(c);
          }
          return Object.values(grouped).flatMap((group) => {
            const n = group.length;
            return group.map((c, i) => {
              const s = positions[`source:${c.source_id}`];
              const t = positions[`target:${c.target_id}`];
              if (!s || !t) return null;
              const x1 = s.x + s.w;
              const y1 = s.y + s.h / 2;
              const x2 = t.x;
              const y2 = t.y + t.h / 2;
              // Stagger control points so fan-out lines don't overlap.
              const offset = n > 1 ? (i - (n - 1) / 2) * 10 : 0;
              const cx = (x1 + x2) / 2;
              const path = `M ${x1} ${y1} C ${cx} ${y1 + offset}, ${cx} ${y2 + offset}, ${x2} ${y2}`;
              const edgeKey = `${c.source_id}:${c.target_id}:${c.target_table || ''}`;
              const isHovered =
                hoveredEdgeKey === edgeKey ||
                hoveredSourceId === c.source_id;
              const isAnyHovered = hoveredEdgeKey || hoveredSourceId != null;
              const baseColor = c.target_table ? '#377AD6' : '#9CA3AF';
              const stroke = isHovered ? baseColor : baseColor;
              const opacity = isAnyHovered && !isHovered ? 0.18 : (c.target_table ? 0.75 : 0.5);
              const strokeWidth = isHovered ? 3 : 1.5;
              return (
                <g key={edgeKey}
                   className="pointer-events-auto cursor-pointer"
                   onClick={() => onClickConnection(c)}
                   onMouseEnter={() => setHoveredEdgeKey(edgeKey)}
                   onMouseLeave={() => setHoveredEdgeKey(null)}>
                  {/* Wide invisible hit area for easier hovering */}
                  <path d={path} stroke="transparent" strokeWidth="14" fill="none" />
                  <path d={path} stroke={stroke}
                        strokeWidth={strokeWidth} fill="none" opacity={opacity}
                        strokeDasharray={c.target_table ? null : '4,4'}
                        style={{ transition: 'opacity 120ms, stroke-width 120ms' }} />
                </g>
              );
            });
          });
        })()}
        {/* In-progress drag line */}
        {dragLine && (
          <path
            d={`M ${dragLine.x1} ${dragLine.y1} L ${dragLine.x2} ${dragLine.y2}`}
            stroke="#377AD6"
            strokeWidth="2"
            strokeDasharray="5,5"
            fill="none"
            opacity={0.8}
          />
        )}
      </svg>

      {/* Left column: sources */}
      <div className="relative flex flex-col gap-3 z-10">
        <ColumnHeader label="Sources" count={sources.length} />
        {sources.map(s => {
          const sourceConnections = connections.filter(c => c.source_id === s.id);
          return (
            <SourceCard
              key={s.id}
              row={s}
              connections={sourceConnections}
              cardRef={(el) => { if (el) cardRefs.current.source.set(s.id, el); else cardRefs.current.source.delete(s.id); }}
              onView={() => onViewSource(s)}
              onDragStart={(e) => onSourceDragStart(s.id, e)}
              onCardHover={(hovered) => setHoveredSourceId(hovered ? s.id : null)}
              hoveredEdgeKey={hoveredEdgeKey}
              onEdgeHover={(c, h) => setHoveredEdgeKey(h ? `${c.source_id}:${c.target_id}:${c.target_table || ''}` : null)}
              onEdgeClick={onClickConnection}
            />
          );
        })}
      </div>

      {/* Right column: targets */}
      <div className="relative flex flex-col gap-3 z-10">
        <ColumnHeader label="Targets" count={targets.length} />
        {targets.map(t => (
          <TargetCard
            key={t.id}
            row={t}
            cardRef={(el) => { if (el) cardRefs.current.target.set(t.id, el); else cardRefs.current.target.delete(t.id); }}
            onView={() => onViewTarget(t)}
          />
        ))}
      </div>
    </div>
  );
}

function ColumnHeader({ label, count }) {
  return (
    <div className="flex items-center justify-between text-xs uppercase tracking-wider text-page-muted font-semibold px-1">
      <span>{label}</span>
      <span>{count}</span>
    </div>
  );
}

// ─── Cards ─────────────────────────────────────────────────────────────
function SourceCard({
  row, cardRef, onView, onDragStart,
  connections = [], onCardHover, hoveredEdgeKey, onEdgeHover, onEdgeClick,
}) {
  const info = adapterInfo(row.kind);
  const Icon = info.icon;
  return (
    <div
      ref={cardRef}
      onMouseEnter={() => onCardHover?.(true)}
      onMouseLeave={() => onCardHover?.(false)}
      className="relative bg-page-bg rounded-[8px] p-3 border border-page-divider hover:border-nav-active transition-colors"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-[6px] flex items-center justify-center shrink-0" style={{ background: info.color + '20', color: info.color }}>
          <Icon size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm truncate">{row.source_name}</div>
          <div className="text-xs text-page-muted">{info.label}</div>
          {row.description && <div className="text-xs text-page-muted truncate mt-1">{row.description}</div>}
        </div>
        <button onClick={onView} className="flex items-center gap-1 px-2 py-1 rounded text-xs hover:bg-card-bg shrink-0">
          <Eye size={12}/> View
        </button>
      </div>
      {connections.length > 0 && (
        <div className="mt-2 pt-2 border-t border-page-divider flex items-center gap-1.5 flex-wrap">
          <ArrowRight size={11} className="text-page-muted shrink-0"/>
          {connections.map(c => {
            const key = `${c.source_id}:${c.target_id}:${c.target_table || ''}`;
            const isHovered = hoveredEdgeKey === key;
            const isUnbound = !c.target_table;
            return (
              <button
                key={key}
                onMouseEnter={() => onEdgeHover?.(c, true)}
                onMouseLeave={() => onEdgeHover?.(c, false)}
                onClick={(e) => { e.stopPropagation(); onEdgeClick?.(c); }}
                title={isUnbound ? 'Unbound connection — click to map' : `${c.target_table} · ${c.mappings.length} field mapping(s)`}
                className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full border transition-colors ${
                  isUnbound
                    ? 'border-page-divider text-page-muted bg-page-bg hover:bg-card-bg'
                    : isHovered
                      ? 'border-nav-active bg-nav-active text-white'
                      : 'border-nav-active/30 text-nav-active bg-nav-active/5 hover:bg-nav-active/10'
                }`}
              >
                <span className="truncate max-w-[140px]">{c.target_table || 'unbound'}</span>
                <span className={isHovered ? 'text-white/80' : 'text-page-muted'}>·</span>
                <span className={isHovered ? 'text-white' : ''}>{c.mappings.length}</span>
              </button>
            );
          })}
        </div>
      )}
      {/* Drag handle on the right edge */}
      <div
        onMouseDown={onDragStart}
        title="Drag to a target to connect"
        className="absolute right-0 top-0 bottom-0 w-3 cursor-ew-resize flex items-center justify-center hover:bg-nav-active/10 rounded-r-[8px]"
      >
        <div className="w-1 h-6 rounded-full bg-page-divider hover:bg-nav-active transition-colors"/>
      </div>
    </div>
  );
}

function TargetCard({ row, cardRef, onView }) {
  const info = adapterInfo(row.kind);
  const Icon = info.icon;
  return (
    <div ref={cardRef} className="relative bg-page-bg rounded-[8px] p-3 border border-page-divider hover:border-nav-active transition-colors">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-[6px] flex items-center justify-center shrink-0" style={{ background: info.color + '20', color: info.color }}>
          <Icon size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm truncate">{row.name}</div>
          <div className="text-xs text-page-muted">{info.label}</div>
          {row.description && <div className="text-xs text-page-muted truncate mt-1">{row.description}</div>}
        </div>
        <button onClick={onView} className="flex items-center gap-1 px-2 py-1 rounded text-xs hover:bg-card-bg shrink-0">
          <Eye size={12}/> View
        </button>
      </div>
    </div>
  );
}

// ─── View modal (Overview / Connection / Schema [/ Sample]) ────────────
function ModalShell({ title, onClose, children, wide }) {
  // Cap the dialog to the viewport (max-h) and let the BODY scroll
  // internally so the header (with the close button) stays visible at
  // the top — was previously scrolling away with long content.
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className={`bg-card-bg rounded-[8px] shadow-xl w-full ${wide ? 'max-w-3xl' : 'max-w-md'} max-h-[90vh] flex flex-col`}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-page-divider shrink-0">
          <h2 className="text-lg font-semibold truncate pr-3">{title}</h2>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-page-bg shrink-0" aria-label="Close"><X size={18}/></button>
        </div>
        <div className="px-5 py-4 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

// In-canvas configuration form for a dropped connector node (Load File, Load/
// Save Postgres, Save Mongo). Renders the catalog's config_schema fields and
// fully creates+configures the source/target from scratch.
function ConnectorConfigModal({ connector, onClose, onSubmit }) {
  const fields = connector?.config_schema?.fields || [];
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (name, v) => setValues(prev => ({ ...prev, [name]: v }));
  const submit = async () => {
    // required check
    for (const f of fields) {
      if (f.required && f.type !== 'file' && !values[f.name]) return;
      if (f.required && f.type === 'file' && !values.__file) return;
    }
    setBusy(true);
    try { await onSubmit(connector, values); } finally { setBusy(false); }
  };
  return (
    <ModalShell title={`Add ${connector.label}`} onClose={onClose}>
      <div className="space-y-3">
        {fields.map(f => (
          <div key={f.name} className="flex flex-col gap-1">
            <label className="text-sm text-page-muted">{f.label}{f.required ? ' *' : ''}</label>
            {f.type === 'file' ? (
              <input type="file" onChange={e => set('__file', e.target.files?.[0] || null)}
                className="text-sm" data-testid={`cfg-${f.name}`} />
            ) : f.type === 'checkbox' ? (
              <input type="checkbox" checked={Boolean(values[f.name])}
                onChange={e => set(f.name, e.target.checked)}
                className="h-4 w-4" data-testid={`cfg-${f.name}`} />
            ) : f.type === 'textarea' ? (
              <textarea
                placeholder={f.placeholder || ''}
                value={values[f.name] ?? ''}
                onChange={e => set(f.name, e.target.value)}
                rows={4}
                className="px-2 py-1 rounded border border-page-divider bg-page-bg text-sm font-mono"
                data-testid={`cfg-${f.name}`}
              />
            ) : (
              <input
                type={f.type === 'password' ? 'password' : f.type === 'number' ? 'number' : 'text'}
                placeholder={f.placeholder || ''}
                value={values[f.name] ?? ''}
                onChange={e => set(f.name, f.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)}
                className="h-9 px-2 rounded border border-page-divider bg-page-bg text-sm"
                data-testid={`cfg-${f.name}`}
              />
            )}
          </div>
        ))}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="h-9 px-3 rounded text-sm hover:bg-page-bg">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="connector-submit"
            className="h-9 px-4 rounded bg-nav-active text-white text-sm hover:opacity-90 disabled:opacity-50">
            {busy ? 'Adding…' : `Add ${connector.label}`}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function ViewModal({ entity, onClose }) {
  const { kind, row } = entity;
  const isSource = kind === 'source';
  const info = adapterInfo(row.kind);
  const [tab, setTab] = useState('overview');
  const [schemaData, setSchemaData] = useState(null);
  const [schemaErr, setSchemaErr] = useState(null);
  const [schemaLoading, setSchemaLoading] = useState(false);

  const loadSchema = useCallback(async () => {
    setSchemaLoading(true); setSchemaErr(null);
    try {
      const data = isSource ? await getSourceSchema(row.id) : await getTargetSchema(row.id);
      setSchemaData(data);
    } catch (e) {
      setSchemaErr(e?.message || 'Failed to discover schema');
    } finally {
      setSchemaLoading(false);
    }
  }, [isSource, row.id]);

  // Schema loads on demand when tab is opened.
  useEffect(() => {
    if (tab === 'schema' && !schemaData && !schemaLoading && !schemaErr) loadSchema();
  }, [tab, schemaData, schemaLoading, schemaErr, loadSchema]);

  const Icon = info.icon;
  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'schema', label: 'Schema' },
    { id: 'records', label: 'Records' },
  ];

  return (
    <ModalShell title={`${isSource ? 'Source' : 'Target'}: ${isSource ? row.source_name : row.name}`} onClose={onClose} wide>
      {/* Header chip */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-[6px] flex items-center justify-center" style={{ background: info.color + '20', color: info.color }}>
          <Icon size={20}/>
        </div>
        <div>
          <div className="font-medium text-sm">{info.label}</div>
          <div className="text-xs text-page-muted">{row.description || '—'}</div>
        </div>
        <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800">{row.status || 'Active'}</span>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-page-divider mb-4">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${tab === t.id ? 'border-nav-active text-nav-active' : 'border-transparent text-page-muted hover:text-page-text'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'overview' && (
        <dl className="grid grid-cols-2 gap-y-3 text-sm">
          <dt className="text-page-muted">Name</dt><dd className="font-medium">{isSource ? row.source_name : row.name}</dd>
          <dt className="text-page-muted">Adapter kind</dt><dd className="font-mono text-xs">{row.kind}</dd>
          <dt className="text-page-muted">Status</dt><dd>{row.status || 'Active'}</dd>
          {row.source_type && (<><dt className="text-page-muted">Source type</dt><dd className="font-mono text-xs">{row.source_type}</dd></>)}
        </dl>
      )}
      {tab === 'schema' && (
        <div>
          {schemaLoading ? (
            <div className="text-page-muted text-sm"><Loader2 className="inline animate-spin mr-2" size={14}/>Discovering schema…</div>
          ) : schemaErr ? (
            <div className="text-red-700 text-sm">{schemaErr}</div>
          ) : !schemaData?.tables?.length ? (
            <div className="text-page-muted text-sm">{schemaData?.note || 'No tables.'}</div>
          ) : (
            <SchemaView schemaData={schemaData} isSource={isSource} />
          )}
        </div>
      )}
      {tab === 'records' && (
        <RecordsTab kind={isSource ? 'source' : 'target'} entityId={row.id} schemaData={schemaData} loadSchema={loadSchema} schemaLoading={schemaLoading} schemaErr={schemaErr} />
      )}
    </ModalShell>
  );
}

// ─── Schema tab content ────────────────────────────────────────────────
export function SchemaView({ schemaData }) {
  const isFile = !!schemaData.file_source;
  return (
    <div className="max-h-[60vh] overflow-y-auto">
      {schemaData.tables.map(t => (
        <div key={t.name} className="border border-page-divider rounded mb-3">
          <div className="px-3 py-2 bg-page-bg flex items-center justify-between text-sm font-medium border-b border-page-divider">
            <span className="font-medium">{t.name}</span>
            <span className="text-xs text-page-muted">{t.columns.length} field{t.columns.length === 1 ? '' : 's'}</span>
          </div>
          {isFile ? (
            <FileSchemaTable columns={t.columns} />
          ) : (
            <table className="w-full text-xs">
              <thead className="text-left text-page-muted">
                <tr className="border-b border-page-divider">
                  <th className="px-3 py-2 font-medium w-[200px]">Field</th>
                  <th className="px-3 py-2 font-medium">Type</th>
                  <th className="px-3 py-2 font-medium">Nullable</th>
                  {t.columns.some(c => c.canonical_name) && <th className="px-3 py-2 font-medium">Maps to</th>}
                </tr>
              </thead>
              <tbody>
                {t.columns.map(c => (
                  <tr key={c.name} className="border-t border-page-divider">
                    <td className="px-3 py-1 font-mono w-[200px]">{c.name}</td>
                    <td className="px-3 py-1 text-page-muted font-mono">{c.type}</td>
                    <td className="px-3 py-1 text-page-muted">{c.nullable ? 'Yes' : 'No'}</td>
                    {t.columns.some(x => x.canonical_name) && (
                      <td className="px-3 py-1 text-page-muted">{c.canonical_name ? <>→ <span className="text-nav-active font-medium">{c.canonical_name}</span></> : ''}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}
    </div>
  );
}

function FileSchemaTable({ columns }) {
  return (
    <table className="w-full text-xs">
      <thead className="text-left text-page-muted">
        <tr className="border-b border-page-divider">
          <th className="px-3 py-2 font-medium">Field</th>
          <th className="px-3 py-2 font-medium">Type</th>
          <th className="px-3 py-2 font-medium">Null %</th>
          <th className="px-3 py-2 font-medium">Distinct</th>
          <th className="px-3 py-2 font-medium">Length</th>
          <th className="px-3 py-2 font-medium">Maps to</th>
          <th className="px-3 py-2 font-medium">Sample values</th>
        </tr>
      </thead>
      <tbody>
        {columns.map(c => {
          const s = c.stats || {};
          const samples = (s.most_common || []).slice(0, 3).map(v => v?.value).filter(v => v != null);
          const lenStr =
            s.min_string_length != null && s.max_string_length != null
              ? (s.min_string_length === s.max_string_length ? `${s.min_string_length}` : `${s.min_string_length}–${s.max_string_length}`)
              : '—';
          return (
            <tr key={c.name} className="border-t border-page-divider first:border-t-0 hover:bg-page-bg/40">
              <td className="px-3 py-2 font-mono align-top">{c.name}</td>
              <td className="px-3 py-2 text-page-muted font-mono align-top">{c.type}</td>
              <td className="px-3 py-2 align-top">
                <NullBar pct={s.null_percentage} />
              </td>
              <td className="px-3 py-2 text-page-muted font-mono align-top">{s.distinct_count ?? '—'}</td>
              <td className="px-3 py-2 text-page-muted font-mono align-top">{lenStr}</td>
              <td className="px-3 py-2 align-top">
                {c.canonical_name ? (
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-800" title={c.transformation_notes || ''}>
                    {c.canonical_name}
                  </span>
                ) : <span className="text-page-muted">—</span>}
              </td>
              <td className="px-3 py-2 align-top">
                <div className="flex flex-wrap gap-1">
                  {samples.length === 0
                    ? <span className="text-page-muted">—</span>
                    : samples.map((v, i) => (
                        <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-page-bg font-mono truncate max-w-[120px]" title={String(v)}>
                          {String(v).length > 20 ? String(v).slice(0, 18) + '…' : String(v)}
                        </span>
                      ))}
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function NullBar({ pct }) {
  if (pct == null) return <span className="text-page-muted text-xs">—</span>;
  const p = Math.max(0, Math.min(100, Number(pct)));
  const color = p === 0 ? 'bg-green-500' : p < 10 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="relative w-16 h-1.5 bg-page-divider rounded-full overflow-hidden">
        <div className={`absolute inset-y-0 left-0 ${color}`} style={{ width: `${p}%` }} />
      </div>
      <span className="text-xs text-page-muted font-mono w-8 text-right">{p}%</span>
    </div>
  );
}

// ─── Connection panel: list field mappings for a source↔target pair ────
function ConnectionPanel({ connection, sources, targets, onClose, onChanged }) {
  const source = sources.find(s => s.id === connection.source_id);
  const target = targets.find(t => t.id === connection.target_id);
  const [removing, setRemoving] = useState(null);
  const [mapperOpen, setMapperOpen] = useState(false);

  const remove = async (edgeId) => {
    setRemoving(edgeId);
    try {
      await deletePipelineEdge(edgeId);
      onChanged();
    } catch (e) {
      alert(`Failed to remove mapping: ${e?.message}`);
    } finally {
      setRemoving(null);
    }
  };

  const titleSuffix = connection.target_table ? ` · ${connection.target_table}` : '';
  return (
    <ModalShell
      title={`${source?.source_name || '?'} → ${target?.name || '?'}${titleSuffix}`}
      onClose={onClose}
      wide
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-page-muted">{connection.mappings.length} field mapping(s)</div>
        <button onClick={() => setMapperOpen(true)}
          className="flex items-center gap-2 px-3 py-1.5 rounded bg-nav-active text-white text-sm">
          <Plus size={14}/> Add mapping
        </button>
      </div>
      {connection.mappings.length === 0 ? (
        <div className="text-page-muted text-sm py-4 text-center">No field mappings yet. Add one to start moving data.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left text-page-muted">
            <tr className="border-b border-page-divider">
              <th className="py-2 font-medium">Source field</th>
              <th className="py-2 font-medium">Canonical element</th>
              <th className="py-2 font-medium">Processing rules</th>
              <th className="py-2 font-medium">Target field</th>
              <th className="py-2 w-[80px]"></th>
            </tr>
          </thead>
          <tbody>
            {connection.mappings.map(m => {
              // Element-side rules (preferred). Fall back to legacy edge-level
              // rule_name for compatibility with old recipes.
              const rules = m.element_rules && m.element_rules.length
                ? m.element_rules
                : (m.rule ? [{ id: m.rule.id, name: m.rule.name, type: m.rule.type }] : []);
              return (
                <tr key={m.edge_id} className="border-b border-page-divider last:border-b-0">
                  <td className="py-2 font-mono text-xs">{m.source_field}</td>
                  <td className="py-2 font-mono text-xs">
                    {m.element ? (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700">
                        {m.element.canonical_name}
                      </span>
                    ) : <span className="text-page-muted">—</span>}
                  </td>
                  <td className="py-2">
                    {rules.length ? (
                      <div className="flex flex-wrap gap-1">
                        {rules.map(r => (
                          <span key={r.id} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                            <Settings2 size={12}/> {r.name}
                          </span>
                        ))}
                      </div>
                    ) : <span className="text-page-muted text-xs">—</span>}
                  </td>
                  <td className="py-2 font-mono text-xs">
                    {m.element && m.element.target_table && m.element.target_column ? (
                      <span title={m.element.fan_out_by_source_field ? 'fan-out by source field' : ''}>
                        <span className="text-page-muted">{m.element.target_table}.</span>
                        <span>{m.element.target_column}</span>
                        {m.element.fan_out_by_source_field && (
                          <span className="ml-1 inline-block text-[9px] px-1 py-0.5 rounded bg-amber-100 text-amber-800 align-middle">
                            fan-out
                          </span>
                        )}
                      </span>
                    ) : (
                      m.target_field || <span className="text-page-muted">(unbound)</span>
                    )}
                  </td>
                  <td className="py-2 text-right">
                    {m.synthetic ? (
                      <span title="Derived from element binding — to remove, unbind the data element"
                            className="text-[10px] text-page-muted px-1 py-0.5 rounded bg-page-bg">
                        derived
                      </span>
                    ) : (
                      <button disabled={removing === m.edge_id} onClick={() => remove(m.edge_id)}
                        className="p-1.5 rounded hover:bg-red-50 text-red-600 disabled:opacity-50" title="Remove mapping">
                        {removing === m.edge_id ? <Loader2 className="animate-spin" size={14}/> : <Trash2 size={14}/>}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {mapperOpen && source && target && (
        <FieldMapperModal
          source={source}
          target={target}
          onClose={() => setMapperOpen(false)}
          onSuccess={() => { setMapperOpen(false); onChanged(); }}
        />
      )}
    </ModalShell>
  );
}

// ─── Field mapper modal: pick source field, optional rule, target field ─
function FieldMapperModal({ source, target, onClose, onSuccess }) {
  const [srcSchema, setSrcSchema] = useState(null);
  const [tgtSchema, setTgtSchema] = useState(null);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [srcTable, setSrcTable] = useState('');
  const [srcField, setSrcField] = useState('');
  const [tgtTable, setTgtTable] = useState('');
  const [tgtField, setTgtField] = useState('');
  const [ruleName, setRuleName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [s, t, r] = await Promise.all([
          getSourceSchema(source.id),
          getTargetSchema(target.id),
          listProcessingRules(),
        ]);
        if (!alive) return;
        setSrcSchema(s);
        setTgtSchema(t);
        setRules(r);
      } catch (e) {
        if (!alive) return;
        setErr(e?.message || 'Failed to load schemas');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [source.id, target.id]);

  const submit = async () => {
    if (!srcField || !tgtField) { alert('Pick a source field and target field'); return; }
    setSubmitting(true);
    try {
      await createPipelineEdge({
        source_id: source.id,
        source_field_name: srcField,
        target_id: target.id,
        target_field_name: tgtField,
        processing_rule_id: ruleName ? (rules.find(r => r.rule_name === ruleName)?.id || null) : null,
      });
      onSuccess();
    } catch (e) {
      alert(`Failed: ${e?.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const srcCols = useMemo(() => {
    const tbl = srcSchema?.tables?.find(t => t.name === srcTable);
    return tbl?.columns || [];
  }, [srcSchema, srcTable]);
  const tgtCols = useMemo(() => {
    const tbl = tgtSchema?.tables?.find(t => t.name === tgtTable);
    return tbl?.columns || [];
  }, [tgtSchema, tgtTable]);

  return (
    <ModalShell title={`Map field: ${source.source_name} → ${target.name}`} onClose={onClose} wide>
      {loading ? (
        <div className="text-page-muted text-sm py-6 text-center"><Loader2 className="inline animate-spin mr-2" size={14}/>Loading schemas…</div>
      ) : err ? (
        <div className="text-red-700 text-sm">{err}</div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Source side */}
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold text-page-muted uppercase">Source</label>
              <select value={srcTable} onChange={e => { setSrcTable(e.target.value); setSrcField(''); }}
                className="h-9 px-2 rounded border border-page-divider bg-page-bg text-sm">
                <option value="">— pick a table —</option>
                {(srcSchema?.tables || []).map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
              </select>
              <select value={srcField} onChange={e => setSrcField(e.target.value)} disabled={!srcTable}
                className="h-9 px-2 rounded border border-page-divider bg-page-bg text-sm disabled:opacity-50">
                <option value="">— pick a field —</option>
                {srcCols.map(c => <option key={c.name} value={c.name}>{c.name} ({c.type})</option>)}
              </select>
            </div>
            {/* Target side */}
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold text-page-muted uppercase">Target</label>
              <select value={tgtTable} onChange={e => { setTgtTable(e.target.value); setTgtField(''); }}
                className="h-9 px-2 rounded border border-page-divider bg-page-bg text-sm">
                <option value="">— pick a table —</option>
                {(tgtSchema?.tables || []).map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
              </select>
              <select value={tgtField} onChange={e => setTgtField(e.target.value)} disabled={!tgtTable}
                className="h-9 px-2 rounded border border-page-divider bg-page-bg text-sm disabled:opacity-50">
                <option value="">— pick a field —</option>
                {tgtCols.map(c => <option key={c.name} value={c.name}>{c.name} ({c.type})</option>)}
              </select>
            </div>
          </div>
          {/* Optional processing rule */}
          <div className="flex flex-col gap-2">
            <label className="text-xs font-semibold text-page-muted uppercase">Optional processing rule</label>
            <select value={ruleName} onChange={e => setRuleName(e.target.value)}
              className="h-9 px-2 rounded border border-page-divider bg-page-bg text-sm">
              <option value="">— none (pass-through) —</option>
              {rules.map(r => <option key={r.id} value={r.rule_name}>{r.rule_name} ({r.rule_type})</option>)}
            </select>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-page-divider">
            <button onClick={onClose} className="px-3 py-1.5 rounded text-sm hover:bg-page-bg">Cancel</button>
            <button disabled={!srcField || !tgtField || submitting} onClick={submit}
              className="px-3 py-1.5 rounded bg-nav-active text-white text-sm font-medium disabled:opacity-50">
              {submitting ? 'Saving…' : 'Save mapping'}
            </button>
          </div>
        </div>
      )}
    </ModalShell>
  );
}

// ─── Records tab: paginated + filterable read from a source/target table ───
// Calls the backend proxy → Flow's cortex.{source,target}.read_records.
// Filters: per-column. Strings default to ilike contains, others to eq.
// Sort: click a column header to toggle asc/desc/off. Pagination: page-size
// selector + Prev/Next. Schema is auto-loaded on first mount so the table
// picker has something to choose from.
export function RecordsTab({ kind, entityId, schemaData, loadSchema, schemaLoading, schemaErr }) {
  const [table, setTable] = useState(null);
  const [filters, setFilters] = useState({});   // { colName: stringValue }
  const [orderBy, setOrderBy] = useState(null); // { field, dir: 'asc'|'desc' }
  const [limit, setLimit] = useState(25);
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // Schema is needed to pick a table and to know column types for filters.
  useEffect(() => {
    if (!schemaData && !schemaLoading && !schemaErr) loadSchema();
  }, [schemaData, schemaLoading, schemaErr, loadSchema]);

  // Pick first table once schema loads.
  useEffect(() => {
    if (!table && schemaData?.tables?.length) setTable(schemaData.tables[0].name);
  }, [schemaData, table]);

  // Parquet storages are element-level (tall) artifacts whose real columns
  // (canonical_name, raw_value, processed_value, …) don't match the storage's
  // carried wide target schema — so for parquet we drive columns off the actual
  // returned rows (endpoint also sends `data.columns`), not the schema. That
  // avoids the "wide schema headers + all-∅ cells" mismatch and de-dupes
  // repeated target columns.
  const isParquetKind = kind === 'parquet_raw' || kind === 'parquet_processed';

  const columns = useMemo(() => {
    if (isParquetKind) {
      const fromData = data?.columns?.length
        ? data.columns
        : (data?.rows?.length ? Object.keys(data.rows[0]) : []);
      return fromData.map(name => ({ name, type: 'string' }));
    }
    if (!schemaData || !table) return [];
    const t = schemaData.tables?.find(x => x.name === table);
    return t?.columns || [];
  }, [isParquetKind, data, schemaData, table]);

  const isStringType = (t) => {
    const s = String(t || '').toLowerCase();
    return s.includes('char') || s.includes('text') || s.includes('string') ||
           s.includes('uuid') || s === 'json' || s === 'jsonb';
  };

  // Build wire filters from the filters state. Empty strings are skipped.
  const wireFilters = useMemo(() => {
    const out = [];
    for (const c of columns) {
      const v = filters[c.name];
      if (v == null || v === '') continue;
      if (isStringType(c.type)) out.push({ field: c.name, op: 'ilike', value: `%${v}%` });
      else out.push({ field: c.name, op: 'eq', value: v });
    }
    return out;
  }, [filters, columns]);

  const fetchRecords = useCallback(async () => {
    if (!table) return;
    setLoading(true); setErr(null);
    try {
      const res = await readRecords(kind, entityId, {
        table,
        filters: wireFilters,
        limit,
        offset,
        order_by: orderBy ? { field: orderBy.field, dir: orderBy.dir } : undefined,
      });
      setData(res);
    } catch (e) {
      setErr(e?.message || 'Failed to read records');
    } finally {
      setLoading(false);
    }
  }, [kind, entityId, table, wireFilters, limit, offset, orderBy]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  // Reset offset when filters/sort/table/limit change.
  useEffect(() => { setOffset(0); }, [table, wireFilters, orderBy, limit]);

  const toggleSort = (field) => {
    if (!orderBy || orderBy.field !== field) setOrderBy({ field, dir: 'asc' });
    else if (orderBy.dir === 'asc') setOrderBy({ field, dir: 'desc' });
    else setOrderBy(null);
  };

  const sortIcon = (field) => {
    if (!orderBy || orderBy.field !== field) return <ArrowUpDown size={11} className="text-page-muted/60"/>;
    return orderBy.dir === 'asc' ? <ArrowUp size={11}/> : <ArrowDown size={11}/>;
  };

  const total = data?.total ?? data?.rows?.length ?? 0;
  const rows = data?.rows || [];
  // Prefer schema column order; fall back to keys of the first row (handy when
  // the endpoint returns a subset of columns, e.g. a SQL projection).
  const resultColumns = useMemo(() => {
    if (columns.length) return columns.map(c => c.name);
    if (data?.columns?.length) return data.columns;
    if (rows.length) return Object.keys(rows[0]);
    return [];
  }, [columns, data, rows]);
  const pageStart = offset + 1;
  const pageEnd = Math.min(offset + rows.length, total);
  const canPrev = offset > 0;
  const canNext = offset + rows.length < total;

  if (schemaLoading) {
    return <div className="text-page-muted text-sm"><Loader2 className="inline animate-spin mr-2" size={14}/>Loading schema…</div>;
  }
  if (schemaErr) {
    return <div className="text-red-700 text-sm">{schemaErr}</div>;
  }
  if (!schemaData?.tables?.length) {
    return <div className="text-page-muted text-sm">{schemaData?.note || 'No tables.'}</div>;
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Controls row: table picker, page-size, pagination */}
      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-xs font-semibold text-page-muted uppercase">Table</label>
        <select value={table || ''} onChange={e => setTable(e.target.value)}
          className="h-8 px-2 rounded border border-page-divider bg-page-bg text-sm">
          {schemaData.tables.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
        </select>

        <label className="text-xs font-semibold text-page-muted uppercase ml-2">Page size</label>
        <select value={limit} onChange={e => setLimit(Number(e.target.value))}
          className="h-8 px-2 rounded border border-page-divider bg-page-bg text-sm">
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>

        <div className="ml-auto flex items-center gap-2 text-xs text-page-muted">
          {loading ? <Loader2 className="animate-spin" size={12}/> : null}
          {total > 0
            ? <span>{pageStart}–{pageEnd} of {total.toLocaleString()}</span>
            : <span>—</span>}
          <button disabled={!canPrev || loading} onClick={() => setOffset(Math.max(0, offset - limit))}
            className="h-7 w-7 inline-flex items-center justify-center rounded border border-page-divider disabled:opacity-40 hover:bg-page-bg">
            <ChevronLeft size={14}/>
          </button>
          <button disabled={!canNext || loading} onClick={() => setOffset(offset + limit)}
            className="h-7 w-7 inline-flex items-center justify-center rounded border border-page-divider disabled:opacity-40 hover:bg-page-bg">
            <ChevronRight size={14}/>
          </button>
        </div>
      </div>

      {err && <div className="text-red-700 text-sm">{err}</div>}

      <div className="max-h-[55vh] overflow-auto border border-page-divider rounded">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-card-bg z-10">
            <tr className="border-b border-page-divider">
              {resultColumns.map(c => (
                <th key={c} className="text-left px-2 py-1.5 font-medium whitespace-nowrap">
                  <button onClick={() => toggleSort(c)}
                    className="inline-flex items-center gap-1 font-mono hover:text-nav-active">
                    {c}
                    {sortIcon(c)}
                  </button>
                </th>
              ))}
            </tr>
            <tr className="border-b border-page-divider bg-page-bg">
              {resultColumns.map(c => {
                const col = columns.find(x => x.name === c);
                const stringy = isStringType(col?.type);
                return (
                  <th key={c} className="px-1.5 py-1 font-normal">
                    <input
                      type="text"
                      value={filters[c] || ''}
                      onChange={e => setFilters({ ...filters, [c]: e.target.value })}
                      placeholder={stringy ? 'contains…' : '= value'}
                      className="w-full h-6 px-1.5 rounded border border-page-divider bg-card-bg text-[11px] font-mono"
                    />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {!loading && rows.length === 0 ? (
              <tr><td colSpan={resultColumns.length} className="text-page-muted text-sm px-3 py-6 text-center">No rows.</td></tr>
            ) : rows.map((r, i) => (
              <tr key={i} className="border-b border-page-divider last:border-b-0 hover:bg-page-bg/40">
                {resultColumns.map(c => (
                  <td key={c} className="px-2 py-1 font-mono whitespace-nowrap max-w-[240px] truncate"
                      title={r[c] == null ? '' : typeof r[c] === 'object' ? JSON.stringify(r[c]) : String(r[c])}>
                    {r[c] === null || r[c] === undefined
                      ? <span className="text-page-muted">∅</span>
                      : typeof r[c] === 'object'
                        ? JSON.stringify(r[c])
                        : String(r[c])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
